import { useState, useEffect, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import { STATES } from './OrderflowBot'
import { openBotJournalReport, exportBotJournal, reportR } from './botJournalReport'
import { useDualWallet } from './DualWalletContext'
import { getAllTokenHoldings } from '../utils/jupiter'
import { useIsPhone } from './hooks/useIsPhone'

// Null-safe fixed formatter — a remote (server) bot's status can arrive
// partial before the first full broadcast, so never call .toFixed on a bare
// field. nf(x, d) returns x.toFixed(d) or a dash placeholder when x is missing.
const nf = (x, d = 1) => (typeof x === 'number' && isFinite(x)) ? x.toFixed(d) : '—'

// Unrealized P/L (signed fraction) of the OPEN position in a status
// payload, or null when flat. dailyPnL is REALIZED-only — it books at
// _fireExit — so a LOADED bot showed +0.0% for the whole life of the
// trade. lastMcap rides every status broadcast (local: every tick;
// server: each bot_status push), and the basis is the blended basis when
// loaded or the pilot entry before that.
const openPnl = (s) => {
  if (!s) return null
  const px = s.lastMcap
  if (!(px > 0)) return null
  const basis = s.load?.blendedBasis || s.pilot?.mcap || 0
  if (!(basis > 0)) return null
  const isLong = (s.load?.direction || s.tradeDirection || 'LONG') !== 'SHORT'
  return ((px - basis) / basis) * (isLong ? 1 : -1)
}

// Chart a token from anywhere in the menu. App.jsx owns the listener
// (setTokenInput + subscribeToToken); the slot list and the journal rows
// both go through this so there's one navigation path, not two.
const chartToken = (address) => {
  if (!address) return
  window.dispatchEvent(new CustomEvent('solcheddar-navigate-token', {
    detail: { address: String(address) }
  }))
}

// Full mint to the clipboard. The async Clipboard API needs a secure
// context (https / localhost) — on plain http it rejects, so fall back to
// the old execCommand path rather than silently doing nothing.
const copyMint = (address) => {
  const text = String(address || '')
  if (!text) return
  const legacy = () => {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    } catch {}
  }
  try {
    const p = navigator.clipboard?.writeText?.(text)
    if (p?.catch) p.catch(legacy); else if (!p) legacy()
  } catch { legacy() }
}

// Phone = the menu stops being a floating popover and becomes a full-bleed
// sheet. useIsPhone is now a device test, not a width one (see the hook), so
// this file no longer keeps its own copy — the old local version flipped the
// menu back into a popover mid-session the moment the phone was rotated.

// ─── User-facing config options ───
const STARTER_SIZES   = [0.01, 0.02, 0.05, 0.10, 0.25, 0.50]
const LOAD_SIZES      = [0.10, 0.25, 0.50, 1.00, 2.00, 5.00]
const STOP_PCTS       = [0.02, 0.03, 0.04, 0.05, 0.08]
const HOLD_MINUTES    = [5, 10, 15, 20, 30, 60]
// Bot bar sizes, in seconds. Was [5,10,15,30] — capped at 30s, which is why
// the server was analysing 5-second bars while the chart showed 1m. Every
// threshold (flushZ, blockMinBars, pilotStopPct) is sane on 1m and is pure
// noise on 5s, so the ceiling WAS the bug, not a convenience limit.
const BAR_SECONDS     = [5, 30, 60, 300, 1800, 14400]
const fmtBars = (sec) => sec >= 3600 ? (sec / 3600) + 'H'
                       : sec >= 60   ? (sec / 60)   + 'M'
                       : sec + 'S'
// Accepts 5s / 30S / 1m / 5M / 4h / 240 (bare = seconds).
const barsIn = (t) => {
  const m = String(t).trim().toLowerCase().match(/^(\d*\.?\d+)\s*([smh])?$/)
  if (!m) return null
  const n = parseFloat(m[1])
  if (!(n > 0)) return null
  const sec = m[2] === 'h' ? n * 3600 : m[2] === 'm' ? n * 60 : n
  return Math.round(sec) >= 1 ? Math.round(sec) : null
}

// Pilot stop: a typed percentage, or hand the exit to VWAP / ATR.
// 'off' is a real mode, not a missing value. Every Pilot-and-below control
// gets it: these are the stages where doing nothing is a legitimate choice
// you might want to test, and the only previous route to it was hand-sending
// a botConfig command. Loaded-stage controls deliberately do NOT get it.
// Initial-stop modes the engine implements (OrderflowBot._planEntry). A
// number = legacy fixed % ('pct'). 'vwap'/'off' were never implemented and
// make no sense with risk-based sizing (no stop = no size), so they're gone.
//   BASE  — under the base low − stop buffer
//   SWING — under the last 1m swing low − stop buffer
//   ATR   — entry − (Entry stop ×) × ATR
const STOP_MODES = ['base', 'swing', 'atr']
const fmtStop = (v) => v === 'base' ? 'BASE' : v === 'swing' ? 'SWING' : v === 'atr' ? 'ATR'
                     : (Number(v) * 100).toFixed(v * 100 % 1 ? 1 : 0) + '%'
const stopIn = (t) => {
  const raw = String(t).trim().toLowerCase()
  if (raw === 'base' || raw === 'swing' || raw === 'atr') return raw
  const n = parseFloat(raw.replace(/[%\s]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return null
  return n > 1 ? n / 100 : n            // 4 -> 0.04, 0.04 -> 0.04
}

// Max hold: minutes, or days/weeks, or "hold until VWAP says otherwise".
const HOLD_OPTS = [5, 15, 60, 240, 1440, 10080, 'off']
const fmtHold = (v) => v === 'off' ? 'OFF'
                     : v >= 10080 ? (v / 10080) + 'w'
                     : v >= 1440  ? (v / 1440)  + 'd'
                     : v >= 60    ? (v / 60)    + 'h'
                     : v + 'm'
const holdIn = (t) => {
  const raw = String(t).trim().toLowerCase()
  if (raw === 'off' || raw === 'none') return 'off'
  const m = raw.match(/^(\d*\.?\d+)\s*([mhdw])?$/)
  if (!m) return null
  const n = parseFloat(m[1])
  if (!(n > 0)) return null
  const mult = { h: 60, d: 1440, w: 10080 }[m[2]] || 1
  return Math.round(n * mult)
}
const ATR_MULTS       = [1.0, 1.5, 2.0, 2.5, 3.0]

// ─── ATR trail (mirrors ATRTrailSettingsMenu / computeATRTrailingStopLive) ───
// Same three parameters the chart indicator exposes, because the bot now runs
// the same formula. The chart's own defaults are 5 / 10 / 2.5.
const ATR_PERIODS     = [3, 5, 8, 10, 14, 21]
const ATR_HHVS        = [5, 10, 15, 20, 30]
const ATR_TRAIL_MULTS = [1.0, 1.5, 2.0, 2.5, 3.0, 4.0]
// OFF is a first-class value on Pilot and below, not a sentinel number. A
// pilot with no trail is a legitimate configuration — it exits on stop or
// max-hold — and the previous menu had no way to say so, which meant the
// only route to "no trail" was a hand-sent botConfig command.
const OFF = 'off'
const withOff = (arr) => [OFF, ...arr]

// ─── Entry gate + management knobs (OrderflowBot config, 2026-10-10) ───
// Typed rows, 'off' allowed where the engine treats null as "check off".
const isOffText = (t) => /^\s*(off|none|-)\s*$/i.test(String(t))
const numPlain = (t) => {
  const n = parseFloat(String(t).replace(/[^0-9.\-]/g, ''))
  return Number.isFinite(n) ? n : null
}
const posNum = (t) => { const n = numPlain(t); return n !== null && n >= 0 ? n : null }
const offOr = (parse) => (t) => isOffText(t) ? OFF : parse(t)
const fmtOff = (fmt) => (v) => v === OFF || v == null ? 'off' : fmt(v)
const fmtPct1 = (v) => +(v * 100).toFixed(1) + '%'
const fmtR = (v) => +Number(v).toFixed(2) + 'R'
const fmtAtr = (v) => +Number(v).toFixed(2) + ' ATR'
// Lock ladder: [[triggerR, lockR], ...] — lockR 0 = breakeven + fee buffer.
const fmtLadder = (steps) => (!Array.isArray(steps) || !steps.length) ? 'off'
  : steps.map(([a, l]) => `${+a}R→${l > 0 ? +l + 'R' : 'BE'}`).join(' ')
const ladderIn = (t) => {
  if (isOffText(t)) return []
  const out = []
  const re = /(\d*\.?\d+)\s*r?\s*(?:→|->|>|:|=)\s*(be|\d*\.?\d+)\s*r?/gi
  let m
  while ((m = re.exec(String(t)))) {
    const a = parseFloat(m[1])
    const l = /be/i.test(m[2]) ? 0 : parseFloat(m[2])
    if (Number.isFinite(a) && Number.isFinite(l) && a > 0 && l >= 0 && l < a) out.push([a, l])
  }
  return out.length ? out.sort((x, y) => x[0] - y[0]) : null
}
const LADDER_PRESETS = [
  [[1, 0], [2, 1]],
  [[1, 0], [2, 1], [3, 2]],
  [[0.5, 0], [1.5, 0.5], [3, 2]],
  [[1.5, 0], [3, 1.5]],
  [],
]

// ─── Foundations (entry-gates-20261018) — [label, configKey, tooltip] ───
const FOUNDATION_FLAGS = [
  ['1 Liquidity',   'ctxLiqBase',
    'Liquidity base: the base qualifies from the Liq window with two-sided volume (buy share ≥ Liq buy, no gap > Liq gap). Off = falls back to the old 5-min volume jump (absorption ≥ 2.0). Skips: LIQ_BASE_SHORT (not enough history yet) / LIQ_BASE_SELLERS / LIQ_BASE_GAP'],
  ['2 Value',       'ctxValue',
    'Buy low in value: enter only at/below the Liq window value-area low (+ Value tol). Off = no location check. Skips: NOT_LOW_IN_VALUE / VALUE_NA (window not full yet)'],
  ['3 Structure',   'ctxStructure',
    'Prior low held: the last-5-min low holds the prior 30-min swing low (or reclaims it within 60s) and delta since that low is positive. Skips: PRIOR_LOW_LOST / NO_DELTA_UP / PRIOR_LOW_NA'],
  ['4 Absorption',  'ctxAbsorb',
    'Absorption cells: a sell-side absorption cell at/near the test low in the last Absorb within, and price has reacted up 0.5 ATR off it. Skips: NO_ABSORB_CELL / ABSORB_NO_REACTION'],
  ['5 Trend',       'ctxTrend',
    'Downtrend veto: skip when the 5-min highs keep stepping down (Lower highs ≥) or the last-30-min POC is 2%+ under the 30 min before. Skips: DOWNTREND / TREND_NA (under 30 min of bars)'],
  ['6 VWAP',        'ctxVwap',
    'Session VWAP: skip when price is more than Max ext ATR above the VWAP of every bar the bot has seen for this coin (since watch start). Skip: EXTENDED_ATR. Off = no VWAP check'],
]
const MARKET_FLAGS = [
  ['Market filter', 'mktFilter',
    'Only enter when the broad market is healthy: SOL 1h change ≥ SOL 1h, SOL 6h change ≥ SOL 6h, and Breadth ≥ (share of Jupiter trending/top-traded coins up over 1h). Skips: MARKET_WEAK / MARKET_NA (no fresh feed)'],
  ['Hot theme',     'mktTheme',
    'Only enter coins whose theme (group like cat/dog/ai/trump/musk, or a shared name/symbol word) is shared by ≥ Theme other coins on Jupiter trending/top-traded in the last 30 min. Skips: NO_HOT_THEME / THEME_NA'],
]
const TIMING_FLAGS = [
  ['Fast trigger',  'ctxFast',
    'Timing: Structure and Absorption read the bot\'s own bars (15s on the droplet) including the forming bar, instead of waiting for 1-minute closes'],
]

// ─── Risk rail options (advanced) ───
const DAILY_LOSS_PCT  = withOff([0.05, 0.10, 0.15, 0.20])
const MAX_TRADES_DAY  = withOff([3, 5, 10, 20])
const COOLDOWN_S      = withOff([30, 60, 120, 300])

// ─── Position detection helpers ─────────────────────────────────────
// Mirrors WalletHoldings.jsx storage convention. Duplicated rather than
// imported to keep the menu standalone — accept the small drift risk
// (this needs to stay in sync if the storage key ever changes).
const DEFAULT_PUMP_SUPPLY = 1_000_000_000

function readSolPosition(mint) {
  if (!mint || mint.startsWith('hl_')) return null
  try {
    const raw = localStorage.getItem(`solcheddar_positions_${mint}`)
    if (!raw) return null
    const positions = JSON.parse(raw)
    if (!Array.isArray(positions) || positions.length === 0) return null

    // Aggregate multiple stored buys into a weighted-by-tokens average mcap
    const totalTokens = positions.reduce((s, p) => s + (p.tokenAmount || 0), 0)
    if (totalTokens <= 0) return null
    const weightedMcap = positions.reduce(
      (s, p) => s + (p.entryMcap || 0) * (p.tokenAmount || 0), 0
    ) / totalTokens
    if (!(weightedMcap > 0)) return null

    // NOTE on size: we do NOT derive SOL value from tokenAmount × mcap. The
    // app's mcap units are ambiguous (chart uses one convention, position
    // records may use another), and a wrong conversion produces sizeSol=0
    // which makes adoptPosition refuse. The bot's management decisions
    // (stop, ATR trail, peakR giveback) all work on price — size is purely
    // informational. So we report `tokenAmount` here and let the caller
    // pass bot.config.loadSizeSol when calling adoptPosition.
    return {
      mcap: weightedMcap,
      tokenAmount: totalTokens,
      direction: 'LONG',                 // Solana spot is always long
      source: 'sol-spot',
      positionCount: positions.length,
    }
  } catch {
    return null
  }
}

function readHlPosition(hlPositions, hlBase) {
  if (!Array.isArray(hlPositions) || !hlBase) return null
  // Match on symbol — try exact, then case-insensitive, then prefix
  const base = String(hlBase).toUpperCase()
  const match = hlPositions.find(p => p.symbol === hlBase)
    || hlPositions.find(p => String(p.symbol || '').toUpperCase() === base)
    || hlPositions.find(p => String(p.symbol || '').toUpperCase().startsWith(base))
  if (!match) return null
  const entryPx = parseFloat(match.avgOpenPrice || 0)
  const qty = Math.abs(parseFloat(match.qty || 0))
  if (!(entryPx > 0) || !(qty > 0)) return null
  return {
    mcap: entryPx,         // for futures, "mcap" is just the entry price
    qty,                   // contract size in the venue's unit
    direction: (match.side || 'LONG').toUpperCase() === 'SHORT' ? 'SHORT' : 'LONG',
    source: 'hl-futures',
  }
}

function Dropdown({ open, children }) {
  if (!open) return null
  return <div className="avwap-dropdown">{children}</div>
}

// ── TypeLine — a type-on line that still carries its presets ──
// Displays fmt(value); focusing turns it into a free text edit AND drops
// the preset list below. Enter/blur commits through parse(); Escape or a
// failed parse reverts. Preset picks go through onMouseDown (fires before
// the input's blur) with a skip-ref so the blur-commit path never also
// commits half-typed text.
function TypeLine({ value, fmt, parse, opts, onCommit, disabled }) {
  const [text, setText] = useState(null)   // null = display mode
  const inputRef = useRef(null)
  const skipBlurRef = useRef(false)
  const editing = text !== null
  return (
    <div className="bot-menu-typeline-wrap">
      <input
        ref={inputRef}
        className="bot-menu-typeline"
        name="cheddar-typeline"
        autoComplete="off"
        value={editing ? text : fmt(value)}
        disabled={disabled}
        onFocus={(e) => { setText(fmt(value)); requestAnimationFrame(() => e.target.select?.()) }}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          else if (e.key === 'Escape') { skipBlurRef.current = true; setText(null); e.currentTarget.blur() }
        }}
        onBlur={() => {
          if (skipBlurRef.current) { skipBlurRef.current = false; return }
          if (!editing) return
          const v = parse(text)
          setText(null)
          if (v !== null && v !== undefined && v !== value) onCommit(v)
        }}
      />
      {editing && !disabled && (
        <div className="avwap-dropdown">
          {opts.map(v => (
            <div key={v} onMouseDown={(e) => {
              e.preventDefault()
              skipBlurRef.current = true
              setText(null)
              if (v !== value) onCommit(v)
              inputRef.current?.blur()
            }}>{fmt(v)}</div>
          ))}
        </div>
      )}
    </div>
  )
}

// TypeLine parsers — forgiving of the display formatting ($, %, K/M, <, unit letters).
const numIn = (t) => {          // money-style: 50k / $1.5M / 250000
  const s = String(t).trim().replace(/[$,\s<%]/g, '')
  const m = s.match(/^(\d*\.?\d+)([kKmM])?$/)
  if (!m) return null
  let n = parseFloat(m[1])
  if (/k/i.test(m[2] || '')) n *= 1e3
  if (/m/i.test(m[2] || '')) n *= 1e6
  return Number.isFinite(n) && n >= 0 ? n : null
}
const intIn = (t) => {          // plain count/time — unit letters ignored ("15m" = 15)
  const n = parseFloat(String(t).replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null
}
const pctIn = (t) => {          // typed as percent, stored as fraction
  const n = parseFloat(String(t).replace(/[^0-9.]/g, ''))
  return Number.isFinite(n) && n >= 0 ? n / 100 : null
}
const winIn = (t) => {          // Jupiter only serves these trending/stats windows
  const v = String(t).trim().toLowerCase()
  return ['5m', '1h', '6h', '24h'].includes(v) ? v : null
}
const fmtUsd = (v) =>           // arbitrary typed values need real formatting, not /1000+'K'
  v >= 1e6 ? '$' + +(v / 1e6).toFixed(1) + 'M'
  : v >= 1000 ? '$' + +(v / 1e3).toFixed(1) + 'K'
  : '$' + Math.round(v)

function fmtMcap(v) {
  if (!v || v <= 0) return '—'
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K'
  if (v >= 1)    return v.toFixed(2)
  return v.toPrecision(3)
}
function fmtDuration(ms) {
  if (!ms || ms < 0) return '—'
  const s = Math.floor(ms / 1000)
  if (s < 60) return s + 's'
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}m ${r}s`
}

// Small robot head for the compact Take over / Auto-pilot header button.
function BotIcon({ size = 12 }) {
  return (
    <svg className="bot-menu-boticon" width={size} height={size} viewBox="0 0 16 16"
         fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
         aria-hidden="true">
      <line x1="8" y1="1.6" x2="8" y2="3.8" />
      <circle cx="8" cy="1.5" r="0.9" fill="currentColor" stroke="none" />
      <rect x="2.5" y="3.8" width="11" height="9" rx="2.2" />
      <circle cx="5.9" cy="7.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10.1" cy="7.8" r="1.1" fill="currentColor" stroke="none" />
      <line x1="6" y1="10.7" x2="10" y2="10.7" />
      <line x1="1" y1="7" x2="1" y2="9.6" />
      <line x1="15" y1="7" x2="15" y2="9.6" />
    </svg>
  )
}

function ScrollLine({ children }) {
  // Wheel-driven horizontal scroll for one Pip-Boy line. React's onWheel is
  // passive (preventDefault ignored) — native non-passive listener required,
  // same trap as the crosshair touchmove. No indicator: the row simply cuts
  // off and the wheel reveals the rest.
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return
      e.preventDefault()
      e.stopPropagation()
      el.scrollLeft += (e.deltaY || e.deltaX)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])
  return <span className="pipboy-scroll" ref={ref}>{children}</span>
}

// Collapsible settings sections: open/closed state survives reloads.
const SEC_KEY = 'solcheddar:botMenuOpen'
const SEC_DEFAULTS = {
  server: true, foundation: true, market: false, sizing: true, pilot: false,
  management: false, risk: false, discovery: false, discoveryAdv: false,
}

function BotSettingsMenu({ show, x, y, bot, onToggle, onUpdate, onReset, onClose, dataSource, hlPair, mode = 'local', onModeChange, onDiscoverToggle, onDryRunToggle, onAnalysisChange, onDiscoverySettings }) {
  const isPhone = useIsPhone()
  const menuRef = useRef(null)
  const [openMenu, setOpenMenu] = useState(null)
  const [pinned, setPinned] = useState(false)
  // Collapsible sections — open/closed per section, persisted.
  const [secOpen, setSecOpen] = useState(() => {
    try { return { ...SEC_DEFAULTS, ...JSON.parse(localStorage.getItem(SEC_KEY) || '{}') } }
    catch { return { ...SEC_DEFAULTS } }
  })
  const toggleSec = (k) => setSecOpen(p => {
    const n = { ...p, [k]: !p[k] }
    try { localStorage.setItem(SEC_KEY, JSON.stringify(n)) } catch {}
    return n
  })
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startOffX: 0, startOffY: 0 })

  // Which journal row's mint was just copied — drives the ✓ flash. Cleared
  // on a timer that's cancelled on unmount so a fast close can't setState
  // on a dead component.
  const [copiedMint, setCopiedMint] = useState('')
  const copiedTimerRef = useRef(null)
  useEffect(() => () => clearTimeout(copiedTimerRef.current), [])
  const flashCopy = (mint) => {
    copyMint(mint)
    setCopiedMint(mint)
    clearTimeout(copiedTimerRef.current)
    copiedTimerRef.current = setTimeout(() => setCopiedMint(''), 1200)
  }

  // ─── Local config mirror ───
  const [showJournal, setShowJournal]     = useState(false)
  const [starterSize, setStarterSize]     = useState(0.05)
  const [loadSize, setLoadSize]           = useState(0.30)
  const [pilotStop, setPilotStop]         = useState(0.04)
  const [pilotHold, setPilotHold]         = useState(20)
  const [atrMult, setAtrMult]             = useState(2.0)   // engine loadAtrMult default
  // ATR trail params. One menu row drives both stages (optclean-20261026);
  // the per-stage engine keys still exist and are kept in sync by that row.
  const [pilotAtrP,   setPilotAtrP]   = useState(5)
  const [pilotAtrH,   setPilotAtrH]   = useState(10)
  const [pilotAtrM,   setPilotAtrM]   = useState(2.0)
  const [loadAtrP,    setLoadAtrP]    = useState(5)
  const [loadAtrH,    setLoadAtrH]    = useState(10)
  const [levelAtrMult, setLevelAtrMult]   = useState(2.0)   // INITIAL stop, not the trail
  const [dailyLoss, setDailyLoss]         = useState(0.10)
  const [maxTrades, setMaxTrades]         = useState(10)
  const [cooldownS, setCooldownS]         = useState(60)

  // ─── Auto-pilot state ───
  // showPositionsList: dropdown showing positions on other tokens
  // pendingAdopt: queued adoption — fires once chart switches to the
  //   target token and detection catches up. Bounded by a 5s timeout.
  const [showPositionsList, setShowPositionsList] = useState(false)
  const [pendingAdopt, setPendingAdopt]           = useState(null)
  const [autoPilotError, setAutoPilotError]       = useState('')

  // ─── Position detection state ───
  // Live HL positions list (futures only). Listens to the same broadcast
  // WalletHoldings consumes — keeps the menu's view in sync with the wallet.
  const [hlPositions, setHlPositions]     = useState([])
  // SOL wallet holdings (mints with non-zero balance). null = haven't
  // received a broadcast yet, so we don't filter. Once we receive the
  // first event, we filter stale localStorage entries against this set.
  const [walletHoldings, setWalletHoldings] = useState(null)
  const heldAmountsRef = useRef(null)  // mint → uiAmount from the last broadcast
  const isFutures = dataSource === 'hyperliquid' && !!hlPair

  // Tick to force re-render so live status updates while panel is open
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!show) return
    const id = setInterval(() => setTick(t => t + 1), 500)
    return () => clearInterval(id)
  }, [show])

  // ─── Listen for HL positions (mirrors WalletHoldings' listener) ───
  // Stays subscribed whenever the menu is mounted; cheap and lets the
  // Auto-pilot button reflect HL position state even before the menu
  // is opened for the first time.
  useEffect(() => {
    if (!isFutures) return
    const handler = (e) => setHlPositions(e.detail || [])
    window.addEventListener('solcheddar-hl-positions', handler)
    return () => window.removeEventListener('solcheddar-hl-positions', handler)
  }, [isFutures])

  // ─── Listen for SOL wallet holdings broadcast ───
  // WalletHoldings dispatches this whenever the user's holdings list
  // changes. We use it to filter stale localStorage position records.
  // Stays mounted regardless of dataSource so the data is ready when
  // the user switches modes.
  useEffect(() => {
    const handler = (e) => {
      setWalletHoldings(e.detail?.mints || [])
      const amts = e.detail?.amounts
      heldAmountsRef.current = Array.isArray(amts)
        ? new Map(amts.map(a => [a.mint, a.uiAmount]))
        : null
    }
    window.addEventListener('solcheddar-wallet-holdings', handler)
    return () => window.removeEventListener('solcheddar-wallet-holdings', handler)
  }, [])

  // ─── Self-sufficient holdings verification ───
  // The filter above depended entirely on WalletHoldings' broadcast — but
  // that panel only fetches while ITS panel is open. A layout without it
  // open never broadcasts, walletHoldings stays null, and the "over-show"
  // fallback lists every localStorage buy record ever written (records are
  // append-only; sells never delete them — verified: no removeItem exists).
  // That's the permanent phantom-position list. Verify against the chain
  // ourselves whenever the menu opens; the broadcast stays as a live
  // supplement when the panel IS open.
  const { publicKey: dwPublicKey } = useDualWallet()
  useEffect(() => {
    if (!show || isFutures || !dwPublicKey) return
    let dead = false
    getAllTokenHoldings(dwPublicKey).then(raw => {
      if (dead || !Array.isArray(raw)) return
      const held = raw.filter(h => (h.uiAmount || 0) > 0)
      setWalletHoldings(held.map(h => h.mint))
      heldAmountsRef.current = new Map(held.map(h => [h.mint, h.uiAmount || 0]))
    }).catch(() => {})
    return () => { dead = true }
  }, [show, isFutures, dwPublicKey])

  // ─── Detect current position for the active token ───
  // Re-derived every render. Reads localStorage for SOL spot or the HL
  // positions array for futures. Returns null if nothing's open.
  // The render-driven recomputation is fine because both reads are O(1)
  // and the menu only re-renders ~2x/sec via the tick above.
  const detectedPosition = (() => {
    if (!bot) return null
    const status = bot.getStatus?.()
    const token = status?.currentToken
    if (!token) return null
    return isFutures
      ? readHlPosition(hlPositions, hlPair?.base)
      : readSolPosition(token)
  })()

  // ─── Enumerate positions on OTHER tokens (SOL spot only) ───
  // Scans localStorage for every solcheddar_positions_* key and returns
  // those that aren't the current token. HL futures pair-switching uses
  // a different surface, so we don't surface other HL symbols here.
  //
  // Stale-entry guard: localStorage keys persist even after positions are
  // closed (cleanup isn't guaranteed across sessions). We cross-reference
  // against walletHoldings — the wallet's actual current balances — to
  // drop entries for tokens the user no longer holds. If we haven't yet
  // received a holdings broadcast (walletHoldings === null), we don't
  // filter — better to over-show than to hide everything during the
  // first second after mount.
  const otherPositions = (() => {
    if (isFutures) return []
    if (!bot) return []
    const currentToken = bot.getStatus?.()?.currentToken
    const heldSet = walletHoldings !== null ? new Set(walletHoldings) : null
    const out = []
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key || !key.startsWith('solcheddar_positions_')) continue
        const mint = key.slice('solcheddar_positions_'.length)
        if (mint === currentToken) continue   // current is handled separately
        // Drop stale entries: if we have a holdings snapshot, the mint
        // must be in it. Pre-broadcast (heldSet=null) we show everything.
        if (heldSet && !heldSet.has(mint)) continue
        const pos = readSolPosition(mint)
        if (!pos) continue
        // Material-fraction test: a record only counts as an OPEN position
        // if the wallet still holds a meaningful share of the recorded
        // size. Presence alone counted every dust remainder — sells done
        // outside the terminal leave crumbs, and 8 crumbs read as "8
        // positions". Under 5% of recorded size = closed, record stale.
        const heldAmt = heldAmountsRef.current?.get(mint)
        if (heldAmt !== undefined && pos.tokenAmount > 0 && heldAmt < pos.tokenAmount * 0.05) continue
        out.push({ ...pos, mint })
      }
    } catch {}
    return out
  })()

  // ─── Deferred adoption ────────────────────────────────────────────
  // When the user picks a position from the "other tokens" dropdown, we
  // dispatch a navigate event and stash the intent in pendingAdopt. This
  // effect fires the actual adoption once the chart has switched to the
  // target token AND detection has caught up. The token check guards
  // against acting on stale state from before the switch.
  useEffect(() => {
    if (!pendingAdopt || !bot) return
    const status = bot.getStatus()
    if (status.currentToken !== pendingAdopt.mint) return
    if (!detectedPosition) return
    if (status.state !== STATES.SCANNING && status.state !== STATES.COOLDOWN) {
      setPendingAdopt(null)
      setAutoPilotError(`Refused — bot is already in ${status.state}`)
      return
    }
    const sizeForBot = detectedPosition.source === 'hl-futures'
      ? detectedPosition.qty
      : bot.config.loadSizeSol
    const res = bot.adoptPosition({
      mcap: detectedPosition.mcap,
      sizeSol: sizeForBot,
      direction: detectedPosition.direction,
    })
    setPendingAdopt(null)
    if (!res.ok) {
      setAutoPilotError(`Refused: ${res.reason}`)
      return
    }
    setAutoPilotError('')
    if (!pinned) onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAdopt, detectedPosition, bot])

  // Safety: if chart never switches (e.g. nav event was ignored), clear
  // the pending intent after 5s rather than block the UI forever.
  useEffect(() => {
    if (!pendingAdopt) return
    const id = setTimeout(() => {
      setPendingAdopt(null)
      setAutoPilotError('Switch timed out — try again')
    }, 5000)
    return () => clearTimeout(id)
  }, [pendingAdopt])

  // ─── Sync local state from bot config on open ───
  useEffect(() => {
    if (show && bot) {
      setStarterSize(bot.config.starterSizeSol)
      setLoadSize(bot.config.loadSizeSol)
      // MODE-AWARE sync. These two read only the numerics for weeks while
      // the runner's default was pilotStopMode:'atr' — the bot was ON ATR
      // and this menu displayed 4% the whole time, then stomped any typed
      // mode on the next open. A display that ignores half the state is a
      // lie, not a default.
      setPilotStop(STOP_MODES.includes(bot.config.pilotStopMode)
        ? bot.config.pilotStopMode : bot.config.pilotStopPct)
      setPilotHold(bot.config.pilotHoldMode === 'off' ? 'off' : bot.config.pilotMaxHoldMin)
      setAtrMult(bot.config.loadAtrMult)
      setPilotAtrP(bot.config.pilotAtrPeriod ?? OFF)
      setPilotAtrH(bot.config.pilotAtrHhvPeriod ?? 10)
      setPilotAtrM(bot.config.pilotAtrMult ?? 2.0)
      setLoadAtrP(bot.config.atrPeriod ?? 5)
      setLoadAtrH(bot.config.atrHhvPeriod ?? 10)
      if (Number.isFinite(bot.config.levelAtrMult)) setLevelAtrMult(bot.config.levelAtrMult)
      setDailyLoss(bot.config.maxDailyLossPct)
      setMaxTrades(bot.config.maxTradesPerDay)
      setCooldownS(bot.config.cooldownMs / 1000)
    }
  }, [show, bot])

  // ─── Click-outside-to-close ───
  useEffect(() => {
    if (!show || pinned) return
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [show, onClose, pinned])

  // ─── Drag handlers (mouse + touch) ───
  useEffect(() => {
    if (!pinned) { setDragOffset({ x: 0, y: 0 }); return }
    const onMove = (e) => {
      if (!dragRef.current.active) return
      const dx = e.clientX - dragRef.current.startX
      const dy = e.clientY - dragRef.current.startY
      setDragOffset({ x: dragRef.current.startOffX + dx, y: dragRef.current.startOffY + dy })
    }
    const onTouchMove = (e) => {
      if (!dragRef.current.active) return
      const t = e.touches[0]
      if (!t) return
      const dx = t.clientX - dragRef.current.startX
      const dy = t.clientY - dragRef.current.startY
      setDragOffset({ x: dragRef.current.startOffX + dx, y: dragRef.current.startOffY + dy })
      e.preventDefault()   // block page scroll while dragging the menu
    }
    const onUp = () => { dragRef.current.active = false }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchmove', onTouchMove, { passive: false })
    window.addEventListener('touchend', onUp)
    window.addEventListener('touchcancel', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onUp)
      window.removeEventListener('touchcancel', onUp)
    }
  }, [pinned])

  const onHeaderMouseDown = (e) => {
    if (!pinned) return
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startOffX: dragOffset.x,
      startOffY: dragOffset.y,
    }
  }

  // ─── Phone: long-press header to pin + begin dragging ───
  const longPressRef = useRef(null)
  const onHeaderTouchStart = (e) => {
    const t = e.touches[0]
    if (!t) return
    const sx = t.clientX, sy = t.clientY
    // 500ms hold with no significant movement = pin & arm drag. A pinch or
    // scroll cancels via touchmove below.
    longPressRef.current = setTimeout(() => {
      setPinned(true)
      dragRef.current = {
        active: true,
        startX: sx,
        startY: sy,
        startOffX: dragOffset.x,
        startOffY: dragOffset.y,
      }
      // Haptic nudge if supported — confirms the grab.
      if (navigator.vibrate) navigator.vibrate(15)
    }, 500)
  }
  const cancelLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null }
  }
  const onHeaderTouchMove = (e) => {
    // If the drag hasn't armed yet, movement means it's a scroll — cancel the
    // pending long-press so we don't hijack the gesture.
    if (!dragRef.current.active) cancelLongPress()
  }

  if (!show || !bot) return null

  const apply = (partial) => {
    onUpdate(partial)
    if (!pinned) onClose()
  }

  // Engine config as the bot reports it (RemoteBot mirrors the server's).
  // These knobs read it directly, so the display is always what's running.
  const cfg = bot.config || {}
  // Pilot vs loaded ATR trail differ? (merged row flags it; live they match)
  const trailSplit = pilotAtrP !== loadAtrP || pilotAtrH !== loadAtrH || pilotAtrM !== atrMult
  // Section header — same look as the old Discovery / Risk rails toggles.
  // A collapsed header shows a one-line summary of what's set inside.
  const secHead = (k, label, summary) => {
    let sum = ''
    if (!secOpen[k] && summary) { try { sum = summary() || '' } catch { sum = '' } }
    return (
      <button className="bot-menu-advanced-toggle bot-menu-sec-toggle" onClick={() => toggleSec(k)}>
        <span className="bot-menu-sec-label">{secOpen[k] ? '▾' : '▸'} {label}</span>
        {sum && <span className="bot-menu-sec-sum">&nbsp;· {sum}</span>}
      </button>
    )
  }
  // rows: [label, key, presets, fmt, parse, title]. OFF ⇄ null in config.
  const knobRows = (rows) => rows.map(([label, key, opts, fmt, parse, title]) => {
    const raw = cfg[key]
    const cur = raw == null ? OFF : raw
    return (
      <div className="bot-menu-row" key={key} title={title}>
        <span className="bot-menu-label">{label}</span>
        <TypeLine
          value={cur} fmt={fmt} parse={parse} opts={opts}
          onCommit={(v) => apply({ [key]: v === OFF ? null : v })}
        />
      </div>
    )
  })

  // ON/OFF rows for boolean engine flags (Foundations). Tap flips it.
  const flagRows = (rows) => rows.map(([label, key, title]) => {
    const on = cfg[key] === true
    return (
      <div className="bot-menu-row" key={key} title={title}>
        <span className="bot-menu-label">{label}</span>
        <div className={`bot-menu-select${on ? '' : ' is-off'}`} onClick={() => apply({ [key]: !on })}>
          {on ? 'ON' : 'off'}
        </div>
      </div>
    )
  })

  const status = bot.getStatus()
  const dcfg = status.discoveryCfg || {}

  // Discovery rows: [label, key, current, presets, fmt, parse] → server.
  const discRows = (rows) => rows.map(([label, key, cur, opts, fmt, parse]) => (
    <div className="bot-menu-row" key={key}>
      <span className="bot-menu-label">{label}</span>
      <TypeLine
        value={cur} fmt={fmt} parse={parse} opts={opts}
        disabled={bot.online === false}
        onCommit={(v) => onDiscoverySettings({ [key]: v })}
      />
    </div>
  ))


  // PORTALED to document.body: born inside the chart cell's subtree, the
  // menu's z-index was trapped in an ancestor stacking context (rail z:10)
  // — "45 inside a 10" competes against sibling cells as a 10. Portal gives
  // it WalletHoldings' citizenship; x/y are clientX/clientY (viewport), so
  // position:fixed is their native currency.
  return createPortal(
    <div
      ref={menuRef}
      className="avwap-toolbar bot-settings-menu"
      style={{
        position: 'fixed',
        // Overlay ladder (see styles.css .app-footer comment): panels < 20.
        // Bot menu sits above all panel chrome but BELOW the footer context
        // (30, carries the wallet holdings/toolbar popovers) and the chart
        // context menu (9990).
        zIndex: 45,
        ...(isPhone
          // PHONE: a full-bleed sheet, not a popover. Pinned to the top-left and
          // stretched edge-to-edge.
          //
          // left:0 + right:0 with width:auto, NOT width:100vw — the used width is
          // then solved from the constraint, so the 8px padding and 1px border are
          // absorbed instead of overflowing. (box-sizing is border-box globally,
          // so 100vw would have worked too, but this can't be broken by someone
          // changing that later. It also dodges 100vw's other trap: on some
          // browsers 100vw counts the scrollbar gutter and is wider than the
          // viewport.)
          //
          // dragOffset is IGNORED here on purpose — a sheet welded to the screen
          // edges has nowhere to be dragged to. The long-press-to-drag handler
          // still runs, it just has nothing to move, so it's inert rather than
          // fighting you.
          //
          // maxHeight + scroll because the menu is TALL with the journal open, and
          // pinning it to top:0 doesn't stop it running off the bottom — it just
          // gives it the most room to do so from. Without this the bottom of the
          // menu is simply unreachable. Scrollbar hidden, per the ask.
          ? {
              left: 0,
              right: 0,
              top: 0,
              width: 'auto',
              minWidth: 0,
              maxHeight: '100dvh',
              overflowY: 'auto',
              borderRadius: 0,
            }
          : {
              left: x + dragOffset.x,
              top: y + dragOffset.y,
              // Fixed width so no state (sections, position, journal) can
              // change it. 300 = original 280 + room for the header buttons.
              width: 300,
              minWidth: 300,
              maxWidth: 300,
            }),
      }}
    >
      <style>{`
        .bot-settings-menu {
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 6px;
          padding: 8px;
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          color: #ddd;
          box-shadow: -10px 10px 20px rgb(0, 0, 0);
        }
        /* The sheet itself scrolls on phone (maxHeight: 100dvh, set inline).
           No bar on that either — it's the frame, not content. */
        .bot-settings-menu {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .bot-settings-menu::-webkit-scrollbar { display: none; }
        .bot-menu-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 6px;
          margin-bottom: 6px;
          border-bottom: 1px solid #2a2a2a;
          cursor: ${pinned ? 'grab' : 'default'};
          user-select: none;
          touch-action: none;
        }
        .bot-menu-title { font-weight: 600; letter-spacing: 0.04em; color: #fff; }
        .journal-btn { padding: 2px 5px; display: inline-flex; align-items: center; }
        .journal-btn .journal-icon { width: 15px; height: 15px; display: block; opacity: 0.85; }
        .journal-btn.on { background: #1e3a5f; border-color: #448aff; }
        .journal-btn.on .journal-icon { opacity: 1; }
        .discover-btn.on { background: #1e3a5f; border-color: #448aff; }
        .discover-btn:disabled { opacity: 0.4; cursor: default; }
        .dryrun-btn.live { background: #3a1414; border-color: #ef5350; }
        .dryrun-btn:disabled { opacity: 0.4; cursor: default; }
        .pipboy-scroll {
          /* width:0 + flex-grow = exactly the leftover space; can never
             push the panel wider whatever the content length. */
          flex: 1 1 0; width: 0; min-width: 0; margin: 0 6px;
          overflow-x: auto; white-space: nowrap; text-align: left;
          scrollbar-width: none; -ms-overflow-style: none;
        }
        .pipboy-scroll::-webkit-scrollbar { display: none; height: 0; }
        /* ── Pip-Boy CRT position display ── */
        .pipboy {
          position: relative;
          margin: 6px 6px 2px;
          background: #041104;
          border: 1px solid #1d4d1d;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: inset 0 0 18px rgba(38, 255, 90, 0.08), 0 0 6px rgba(38, 255, 90, 0.06);
        }
        .pipboy-scan {
          pointer-events: none;
          position: absolute; inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            rgba(38, 255, 90, 0.05) 0px,
            rgba(38, 255, 90, 0.05) 1px,
            transparent 1px,
            transparent 3px
          );
          animation: pipboy-flicker 4s infinite;
        }
        @keyframes pipboy-flicker {
          0%, 100% { opacity: 1; }
          97% { opacity: 1; }
          98% { opacity: 0.7; }
          99% { opacity: 0.95; }
        }
        .pipboy-body {
          position: relative;
          padding: 6px 8px;
          font-family: 'JetBrains Mono', 'Consolas', monospace;
          font-size: 10px;
          line-height: 1.5;
          color: #4dff7a;
          text-shadow: 0 0 4px rgba(38, 255, 90, 0.55);
        }
        .pipboy-row { display: flex; justify-content: space-between; gap: 10px; white-space: nowrap; }
        .pipboy-title {
          font-size: 10.5px; font-weight: 700; letter-spacing: 0.08em;
          border-bottom: 1px solid #1d4d1d; padding-bottom: 3px; margin-bottom: 3px;
        }
        .pipboy-dim { color: #2f9e51; text-shadow: 0 0 3px rgba(38, 255, 90, 0.3); }
        .pipboy-blink { animation: pipboy-blink 1.1s steps(1) infinite; }
        @keyframes pipboy-blink { 50% { opacity: 0.15; } }
        .bot-journal { padding: 8px; max-height: 340px; display: flex; flex-direction: column; }
        .bot-journal-empty { color: #666; font-size: 11px; text-align: center; padding: 24px 0; }
        .bot-journal-summary { display: flex; justify-content: space-between; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #aaa; padding: 4px 6px 8px; border-bottom: 1px solid #2a2a2a; }
        .bot-journal-summary .pos { color: #66bb6a; } .bot-journal-summary .neg { color: #ef5350; }
        .bot-journal-tools { display: flex; gap: 6px; padding: 6px 6px 2px; }
        .bjt-btn { flex: 1; background: #161d28; color: #c3ccd9; border: 1px solid #2a3444; border-radius: 3px; font-size: 10px; font-weight: 700; padding: 5px 0; cursor: pointer; text-transform: uppercase; letter-spacing: 0.03em; transition: all 120ms; font-family: inherit; }
        .bjt-btn:hover { background: #1e2836; border-color: #448aff; color: #fff; }
        /* The journal list scrolls, but shows no bar — house pattern, same as
           .trades-container / .wl-rows / .scan-panel. (The "Report" button opens
           the full expectancy analysis in a NEW TAB; this is the in-menu list.) */
        .bot-journal-list {
          overflow-y: auto;
          margin-top: 6px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .bot-journal-list::-webkit-scrollbar { display: none; }
        .bot-journal-row { padding: 5px 6px; border-left: 2px solid #444; margin-bottom: 4px; background: #161616; border-radius: 2px; }
        .bot-journal-row.win { border-left-color: #66bb6a; } .bot-journal-row.loss { border-left-color: #ef5350; }
        .bjr-top { display: flex; align-items: center; gap: 6px; font-size: 11px; }
        .bjr-sym { font-weight: 700; color: #f3f3f3; }
        .bjr-dir { font-size: 9px; font-weight: 700; padding: 0 3px; border-radius: 2px; }
        .bjr-dir.long { background: #1e3a2a; color: #66bb6a; } .bjr-dir.short { background: #3a1e1e; color: #ef5350; }
        .bjr-pnl, .bjr-r { margin-left: auto; font-weight: 700; font-variant-numeric: tabular-nums; }
        .bjr-r { margin-left: 0; min-width: 34px; text-align: right; }
        .bjr-pnl.pos, .bjr-r.pos { color: #66bb6a; } .bjr-pnl.neg, .bjr-r.neg { color: #ef5350; }
        .bjr-bot { display: flex; gap: 8px; font-size: 9px; color: #888; margin-top: 2px; font-variant-numeric: tabular-nums; }
        .bjr-reason { color: #6b7a8f; text-transform: uppercase; letter-spacing: 0.03em; }
        .bjr-dur { margin-left: auto; color: #666; }
        /* A journal row the runner tagged with a mint is a link to that
           coin's chart — same navigation as the slot list above. Rows with
           no mint (Hyperliquid trades, pre-tagging history) stay inert, so
           the affordance only appears where the click does something. */
        .bot-journal-row.clickable { cursor: pointer; }
        .bot-journal-row.clickable:hover { background: #1c2330; }
        .bjr-sym.link { color: #8eb6ff; text-decoration: underline; text-decoration-color: #33415a; }
        .bot-journal-row.clickable:hover .bjr-sym.link { color: #b9d2ff; text-decoration-color: #8eb6ff; }
        .bjr-copy {
          flex: 0 0 auto;
          background: transparent;
          border: 1px solid #2a3444;
          border-radius: 3px;
          color: #6b7a8f;
          font-size: 9px;
          line-height: 1;
          padding: 2px 4px;
          cursor: pointer;
          opacity: 0.45;
          font-family: inherit;
          transition: opacity 120ms, color 120ms, border-color 120ms;
        }
        .bot-journal-row:hover .bjr-copy { opacity: 1; }
        /* Phone sheet: there is no hover to reveal it, so don't hide it. */
        @media (hover: none) { .bjr-copy { opacity: 0.85; } }
        .bjr-copy:hover { color: #8eb6ff; border-color: #448aff; }
        .bjr-copy.copied { color: #66bb6a; border-color: #2f6a3a; opacity: 1; }
        .bot-menu-header { flex-wrap: nowrap; gap: 6px; }
        .bot-menu-title { flex: 0 0 auto; }
        .bot-menu-actions { display: flex; gap: 4px; flex-wrap: nowrap; min-width: 0; align-items: center; }
        .bot-menu-actions > * { flex: 0 0 auto; }
        .bot-menu-btn.auto-pilot {
          display: inline-flex; align-items: center; justify-content: center; gap: 2px;
          padding: 2px 6px; line-height: 1;
        }
        .bot-menu-boticon { display: block; }
        .bot-menu-boticon-n { font-size: 9px; }
        .bot-menu-btn {
          background: #222;
          border: 1px solid #333;
          color: #ddd;
          padding: 2px 8px;
          border-radius: 3px;
          font-size: 10px;
          cursor: pointer;
          white-space: nowrap;
        }
        .bot-menu-btn:hover { background: #2a2a2a; }
        .bot-menu-btn.on { background: #00c85333; border-color: #00c85388; color: #66e09f; }
        .bot-menu-btn.auto-pilot {
          background: #448aff22;
          border-color: #448aff66;
          color: #8eb6ff;
        }
        .bot-menu-btn.auto-pilot:hover { background: #448aff44; }
        .bot-menu-btn.release {
          background: #ffb30022;
          border-color: #ffb30066;
          color: #ffce5c;
        }
        .bot-menu-btn.release:hover { background: #ffb30044; }
        .bot-menu-btn.auto-pilot:disabled {
          opacity: 0.6;
          cursor: wait;
        }
        .bot-menu-positions-list {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 4px;
          background: #0d1929;
          border: 1px solid #2c4a7a;
          border-radius: 4px;
          padding: 6px;
          min-width: 220px;
          max-height: 280px;
          overflow-y: scroll;          /* always show — auto hides it on macOS */
          z-index: 100;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6);
        }
        /* Explicit scrollbar styling — WebKit hides scrollbars when idle on
           macOS by default. Force a visible track + thumb so the user knows
           the list scrolls when there are more than ~7 positions. */
        .bot-menu-positions-list::-webkit-scrollbar {
          width: 8px;
        }
        .bot-menu-positions-list::-webkit-scrollbar-track {
          background: #1a3460;
          border-radius: 4px;
        }
        .bot-menu-positions-list::-webkit-scrollbar-thumb {
          background: #448aff;
          border-radius: 4px;
        }
        .bot-menu-positions-list::-webkit-scrollbar-thumb:hover {
          background: #5a9aff;
        }
        /* Firefox */
        .bot-menu-positions-list {
          scrollbar-width: thin;
          scrollbar-color: #448aff #1a3460;
        }
        .bot-menu-positions-title {
          font-size: 9px;
          letter-spacing: 0.08em;
          color: #8eb6ff;
          text-transform: uppercase;
          padding: 4px 6px 8px;
          font-weight: 600;
          border-bottom: 1px solid #1a3460;
          margin-bottom: 4px;
        }
        .bot-menu-positions-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 3px;
          cursor: pointer;
          font-size: 11px;
        }
        .bot-menu-positions-item:hover { background: #1a3460; }
        .bot-menu-positions-side {
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0.04em;
          flex: 0 0 12px;
        }
        .bot-menu-positions-mint {
          color: #ccc;
          font-family: 'JetBrains Mono', monospace;
          flex: 1;
        }
        .bot-menu-positions-detail {
          color: #8eb6ff;
          font-size: 10px;
        }
        /* Slot list (server coordinator) — reuses the positions row pattern
           but rendered inline, not as a dropdown. */
        .bot-slot-list {
          position: static;
          margin: 6px 0 0;
          box-shadow: none;
          min-width: 0;
          max-height: 160px;
        }
        .bot-slot-stop {
          flex: 0 0 auto;
          background: transparent;
          border: 1px solid #3a2a2a;
          border-radius: 3px;
          color: #ff7878;
          font-size: 9px;
          line-height: 1;
          padding: 3px 5px;
          cursor: pointer;
        }
        .bot-slot-stop:hover { background: #5a1a1a; border-color: #ff7878; }
        .bot-menu-autopilot-err {
          margin: 4px 6px;
          padding: 4px 8px;
          background: #5a1a1a;
          border: 1px solid #882222;
          border-radius: 3px;
          color: #ff7878;
          font-size: 10px;
        }
        .bot-menu-section {
          margin: 6px 0;
          padding: 4px 6px;
        }
        .bot-menu-section-title {
          font-size: 9px;
          letter-spacing: 0.08em;
          color: #888;
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        .bot-menu-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 3px 0;
          font-size: 11px;
        }
        /* Three dropdowns on one line: ATR / HHV / MULT, the same trio the
           chart's ATRTrailSettingsMenu shows, in the same order. Grouped so
           they read as one indicator rather than three unrelated knobs. */
        .bot-menu-atr3 { display: flex; align-items: center; gap: 3px; }
        .bot-menu-atr3 .bot-menu-select { min-width: 42px; justify-content: center; }
        .bot-menu-atr3 .atr3-sep { color: #444; font-size: 9px; }
        .bot-menu-select.is-off { color: #666; font-style: italic; }
        .bot-menu-label { color: #aaa; }
        .bot-menu-value { color: #fff; font-weight: 500; }
        .bot-menu-select {
          position: relative;
          background: #222;
          border: 1px solid #333;
          border-radius: 3px;
          padding: 2px 8px;
          color: #ddd;
          cursor: pointer;
          font-size: 10px;
          min-width: 70px;
          text-align: right;
        }
        .bot-menu-select:hover { background: #2a2a2a; }
        .bot-menu-typeline-wrap { position: relative; min-width: 70px; }
        .bot-menu-typeline {
          width: 70px;
          background: transparent;
          border: none;
          border-bottom: 1px solid #333;
          border-radius: 0;
          color: #ddd;
          font-size: 10px;
          text-align: right;
          padding: 2px 2px;
          outline: none;
        }
        .bot-menu-typeline:focus { border-bottom-color: #448aff; color: #fff; }
        .bot-menu-typeline:disabled { opacity: 0.4; }
        .avwap-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          background: #1a1a1a;
          border: 1px solid #333;
          border-radius: 3px;
          z-index: 10;
          min-width: 70px;
        }
        .avwap-dropdown div {
          padding: 4px 10px;
          cursor: pointer;
          font-size: 10px;
        }
        .avwap-dropdown div:hover { background: #2a2a2a; }
        .bot-menu-live {
          background: #0d0d0d;
          border-radius: 4px;
          padding: 6px 8px;
          margin: 6px 0;
        }
        .bot-menu-live-row {
          display: flex;
          justify-content: space-between;
          padding: 2px 0;
          font-size: 10px;
          color: #aaa;
        }
        .bot-menu-live-row .v { color: #fff; font-weight: 500; }
        .bot-menu-live-row .v.qual { color: #66e09f; }
        .bot-menu-live-row .v.warn { color: #ffb300; }
        .bot-menu-advanced-toggle {
          width: 100%;
          background: transparent;
          border: none;
          color: #666;
          font-size: 10px;
          padding: 4px 6px;
          cursor: pointer;
          text-align: left;
        }
        .bot-menu-advanced-toggle:hover { color: #aaa; }
        /* Section headers are BLOCK-level flex rows. As inline-block buttons
           (the browser default), consecutive collapsed headers sat on one
           line in the menu's shrink-to-fit width calc, so their widths
           SUMMED: every collapse made the menu wider. */
        .bot-menu-sec-toggle {
          display: flex;
          align-items: baseline;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          border-top: 1px solid #1e1e1e;
        }
        .bot-menu-sec-label { flex: 0 0 auto; }
        .bot-menu-sec-toggle + .bot-menu-section { margin-top: 0; }
        .bot-menu-sec-sum {
          /* width:0 + flex-grow: takes leftover space only, never widens the menu */
          flex: 1 1 0; width: 0; min-width: 0;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          color: #4e4e4e;
        }
        .bot-menu-sec-toggle:hover .bot-menu-sec-sum { color: #777; }
      `}</style>

      {/* Header */}
      <div
        className="bot-menu-header"
        onMouseDown={onHeaderMouseDown}
        onTouchStart={onHeaderTouchStart}
        onTouchMove={onHeaderTouchMove}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
      >
        <span className="bot-menu-title">BOT</span>
        <div className="bot-menu-actions">
          <button
            className={`bot-menu-btn journal-btn ${showJournal ? 'on' : ''}`}
            onClick={() => setShowJournal(v => !v)}
            onMouseDown={(e) => e.stopPropagation()}
            title="Trade journal"
          >
            <img src="/journal.png" alt="Journal" className="journal-icon" draggable="false" />
          </button>
          {/* Auto-pilot button — three states:
                1. Position on current token → "Take over @ X" (one-click adopt)
                2. No position on current, but N on other tokens → opens dropdown
                3. No positions anywhere → hidden
              No manual form — "auto-pilot" only makes sense when there's
              actually a position to adopt. */}
          {bot.enabled && (status.state === STATES.SCANNING || status.state === STATES.COOLDOWN) && (
            (detectedPosition || otherPositions.length > 0) && (
              <div style={{ position: 'relative' }}>
                <button
                  className="bot-menu-btn auto-pilot"
                  disabled={!!pendingAdopt}
                  onClick={() => {
                    setAutoPilotError('')
                    if (detectedPosition) {
                      // Direct adopt — position is on the current token.
                      // Size is informational; bot management works on price.
                      const sizeForBot = detectedPosition.source === 'hl-futures'
                        ? detectedPosition.qty
                        : bot.config.loadSizeSol
                      const res = bot.adoptPosition({
                        mcap: detectedPosition.mcap,
                        sizeSol: sizeForBot,
                        direction: detectedPosition.direction,
                      })
                      if (!res.ok) {
                        setAutoPilotError(`Refused: ${res.reason}`)
                        return
                      }
                      if (!pinned) onClose()
                      return
                    }
                    // No position on current token but others exist —
                    // toggle the dropdown list.
                    setShowPositionsList(s => !s)
                  }}
                  title={
                    pendingAdopt
                      ? 'Switching…'
                      : detectedPosition
                        ? `Take over @ ${detectedPosition.mcap >= 1000
                            ? (detectedPosition.mcap / 1000).toFixed(1) + 'K'
                            : detectedPosition.mcap.toFixed(2)}`
                          + ` · adopt the open ${detectedPosition.direction.toLowerCase()} on this token`
                        : `Auto-pilot · pick from ${otherPositions.length} open position${otherPositions.length === 1 ? '' : 's'}`
                  }
                  aria-label={detectedPosition ? 'Take over position' : 'Auto-pilot'}
                >
                  {pendingAdopt
                    ? '⏳'
                    : detectedPosition
                      ? <BotIcon />
                      : <><BotIcon /><span className="bot-menu-boticon-n">{otherPositions.length}</span></>}
                </button>

                {/* Other-positions dropdown — only when nothing detected on
                    current token. Clicking an entry navigates the chart and
                    queues adoption via the pendingAdopt effect. */}
                {showPositionsList && !detectedPosition && otherPositions.length > 0 && (
                  <div className="bot-menu-positions-list">
                    <div className="bot-menu-positions-title">
                      Open positions ({otherPositions.length})
                    </div>
                    {otherPositions.map((pos) => {
                      const label = pos.mint.length > 8
                        ? `${pos.mint.slice(0, 4)}…${pos.mint.slice(-4)}`
                        : pos.mint
                      const tok = pos.tokenAmount >= 1e6
                        ? (pos.tokenAmount / 1e6).toFixed(1) + 'M'
                        : pos.tokenAmount >= 1e3
                          ? (pos.tokenAmount / 1e3).toFixed(1) + 'K'
                          : pos.tokenAmount.toFixed(0)
                      return (
                        <div
                          key={pos.mint}
                          className="bot-menu-positions-item"
                          onClick={() => {
                            setShowPositionsList(false)
                            setAutoPilotError('')
                            // Queue the adoption; effect will fire when bot
                            // sees this token. We don't capture mcap here
                            // because the chart's just-loaded data is the
                            // source of truth — we want fresh detection.
                            setPendingAdopt({
                              mint: pos.mint,
                              direction: pos.direction,
                            })
                            // Switch the chart
                            window.dispatchEvent(new CustomEvent('solcheddar-navigate-token', {
                              detail: { address: pos.mint }
                            }))
                          }}
                        >
                          <span className="bot-menu-positions-side"
                            style={{ color: pos.direction === 'SHORT' ? '#ff7878' : '#66e09f' }}>
                            {pos.direction === 'SHORT' ? 'S' : 'L'}
                          </span>
                          <span className="bot-menu-positions-mint">{label}</span>
                          <span className="bot-menu-positions-detail">
                            {tok} @ {fmtMcap(pos.mcap)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          )}
          {bot.enabled && (status.state === STATES.LOADED || status.state === STATES.PILOT) && (
            <button
              className="bot-menu-btn release"
              onClick={() => {
                if (window.confirm('Release the bot from this position? You\'ll manage it manually from here. No exit signal will fire.')) {
                  bot.releasePosition()
                  if (!pinned) onClose()
                }
              }}
              title="Stop managing — take back manual control"
            >
              Release
            </button>
          )}
          {onModeChange && (
            <button
              className="bot-menu-btn"
              onClick={() => onModeChange(mode === 'server' ? 'local' : 'server')}
              title={mode === 'server'
                ? (bot.online ? 'Server bot (runner online) — click for local' : 'Server bot (runner OFFLINE) — click for local')
                : 'Local bot (runs in this tab) — click for server'}
              style={mode === 'server' && bot.online === false ? { opacity: 0.5 } : undefined}
            >
              {mode === 'server' ? (bot.online === false ? '☁️✕' : '☁️') : '💻'}
            </button>
          )}
          <button
            className={`bot-menu-btn ${bot.enabled ? 'on' : ''}`}
            onClick={onToggle}
          >
            {bot.enabled ? 'ON' : 'OFF'}
          </button>
          <button className="bot-menu-btn" onClick={() => setPinned(p => !p)}>
            {pinned ? '📌' : '📍'}
          </button>
          <button className="bot-menu-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* ── Pip-Boy position display + live read (merged) ── */}
      <div className="pipboy">
        <div className="pipboy-scan" />
        {(() => {
          const inPos = status.state === STATES.PILOT || status.state === STATES.LOADED
          const dir = status.load?.direction || status.pilot?.direction || 'LONG'
          const tok = status.currentToken ? String(status.currentToken).slice(0, 6) : '------'
          const cd = status.cooldownRemaining > 0 ? ` ${Math.ceil(status.cooldownRemaining / 1000)}s` : ''
          const b = status.activeBase
          return (
            <div className="pipboy-body">
              <div className="pipboy-row pipboy-title">
                <span>◉ {inPos ? `${status.state === STATES.PILOT ? 'PILOT' : 'LOADED'} · ${dir}` : 'POSITION MONITOR'}</span>
                {inPos
                  ? <span className="pipboy-blink">●</span>
                  : <span className="pipboy-dim">{bot.enabled ? 'ARMED' : 'STANDBY'}</span>}
              </div>
              <div className="pipboy-row">
                <span className="pipboy-dim">STATUS</span>
                <span>{status.state}{status.load?.adopted ? ' (AUTO)' : ''}{cd}</span>
              </div>
              <div className="pipboy-row">
                <span className="pipboy-dim">TOKEN</span><span>{tok}</span>
              </div>
              <div className="pipboy-row">
                <span className="pipboy-dim">CHURN</span>
                <span className={status.churnScore >= 5 ? '' : 'pipboy-dim'}>{nf(status.churnScore, 1)}</span>
              </div>
              {detectedPosition && (status.state === STATES.SCANNING || status.state === STATES.COOLDOWN) && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">OPEN POS</span>
                  <span>
                    {detectedPosition.direction === 'SHORT' ? 'S' : 'L'}
                    {' '}
                    {detectedPosition.source === 'hl-futures'
                      ? `${detectedPosition.qty}`
                      : `${detectedPosition.tokenAmount >= 1e6
                          ? (detectedPosition.tokenAmount / 1e6).toFixed(1) + 'M'
                          : detectedPosition.tokenAmount >= 1e3
                            ? (detectedPosition.tokenAmount / 1e3).toFixed(1) + 'K'
                            : detectedPosition.tokenAmount.toFixed(0)} tok`}
                    {' @ '}{fmtMcap(detectedPosition.mcap)}
                    {detectedPosition.positionCount > 1 ? ` (${detectedPosition.positionCount}×)` : ''}
                  </span>
                </div>
              )}
              {b ? (
                <>
                  <div className="pipboy-row">
                    <span className="pipboy-dim">BASE</span>
                    <span>{fmtDuration(b.durationMs)} · {fmtMcap(b.lowMcap)}–{fmtMcap(b.highMcap)}</span>
                  </div>
                  <div className="pipboy-row">
                    <span className="pipboy-dim">ABS/PEAK</span>
                    <span>{nf(b.absorption, 1)}× · {nf(b.peakChurn, 1)}</span>
                  </div>
                  <div className="pipboy-row">
                    <span className="pipboy-dim">QUALIFIED</span>
                    <span className={b.qualified ? '' : 'pipboy-dim'}>{b.qualified ? 'YES' : 'BUILDING…'}</span>
                  </div>
                  {b.qualified && !inPos && status.entryGate && (
                    <div className="pipboy-row" title="Entry location gate — last read">
                      <span className="pipboy-dim">GATE</span>
                      <span className={status.entryGate.pass ? '' : 'pipboy-dim'}>
                        {status.entryGate.pass ? '✓ ' : '✗ '}{status.entryGate.why}
                        {Number.isFinite(status.entryGate.basePos) ? ` · ${nf(status.entryGate.basePos * 100, 0)}%` : ''}
                        {Number.isFinite(status.entryGate.extAtr) ? ` · ${nf(status.entryGate.extAtr, 1)}ATR` : ''}
                      </span>
                    </div>
                  )}
                </>
              ) : !inPos && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">BASE</span><span className="pipboy-dim">NONE</span>
                </div>
              )}
              {status.pilot && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">ENTRY</span>
                  <span>{fmtMcap(status.pilot.mcap)} · {fmtDuration(status.pilot.timeRemainingMs)}</span>
                </div>
              )}
              {status.load && (
                <>
                  <div className="pipboy-row">
                    <span className="pipboy-dim">BASIS</span>
                    <span>{fmtMcap(status.load.blendedBasis)}{status.load.adopted ? ' (AUTO)' : ''}</span>
                  </div>
                  <div className="pipboy-row">
                    <span className="pipboy-dim">PEAK</span>
                    <span>{nf(status.load.peakR, 2)}R</span>
                  </div>
                </>
              )}
              {status.position && (status.pilot || status.load) && (
                <div className="pipboy-row" title="Effective stop · lock · trail · open share">
                  <span className="pipboy-dim">STOP</span>
                  <span>
                    {fmtMcap(status.position.effStopMcap)}
                    {status.position.lockStopMcap > 0 ? ` · ${status.position.lockR > 0 ? '+' + nf(status.position.lockR, 1) + 'R' : 'BE'}` : ''}
                    {status.position.trailArmed ? ' · trail' : ''}
                    {status.position.remainFrac < 1 ? ` · ${nf(status.position.remainFrac * 100, 0)}% left` : ''}
                  </span>
                </div>
              )}
              {(status.pilot || status.load) && (() => {
                const pl = openPnl(status)
                if (pl === null) return null
                return (
                  <div className="pipboy-row">
                    <span className="pipboy-dim">P/L</span>
                    <span style={pl < 0 ? { color: '#ff6b6b', textShadow: '0 0 4px rgba(255,80,80,0.6)' } : undefined}>
                      {(pl >= 0 ? '+' : '') + nf(pl * 100, 2)}%
                    </span>
                  </div>
                )
              })()}
              {mode === 'server' && status.lastScan && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">SCAN</span>
                  <ScrollLine>{status.lastScan.line}</ScrollLine>
                  <span style={{ flex: 'none' }}>{Math.max(0, Math.round((Date.now() - status.lastScan.ts) / 1000))}s</span>
                </div>
              )}
              {mode === 'server' && status.watchStartedAt > 0 && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">WATCH</span>
                  <span>
                    {status.watchSource === 'auto' ? '🔎 auto' : 'manual'}
                    {' · '}{Math.floor((Date.now() - status.watchStartedAt) / 60000)}m
                    {status.watchPinned ? ' · pinned' : ''}
                  </span>
                </div>
              )}
              {mode === 'server' && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">EXEC</span>
                  <span style={status.dryRun ? undefined : { color: '#ff6b6b', textShadow: '0 0 4px rgba(255,80,80,0.6)' }}>
                    {status.dryRun ? 'DRY RUN' : 'LIVE'}
                    {' · '}{status.discovery ? 'HUNTING' : 'MANUAL'}
                  </span>
                </div>
              )}
              {status.avwapLow > 0 && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">AVWAP⌊</span>
                  <span>{fmtMcap(status.avwapLow)}{status.avwapStopMcap > 0 ? ` · stop ${fmtMcap(status.avwapStopMcap)}` : ''}</span>
                </div>
              )}
              {status.reentryArmed && (
                <div className="pipboy-row">
                  <span className="pipboy-dim">REENTRY</span>
                  <span className="pipboy-blink">{Math.ceil((status.reentryRemainingMs || 0) / 1000)}s window</span>
                </div>
              )}
              <div className="pipboy-row">
                <span className="pipboy-dim">DAY</span>
                <span>{status.dailyTradeCount}T · {(status.dailyPnL >= 0 ? '+' : '') + nf(status.dailyPnL * 100, 1)}%</span>
              </div>
            </div>
          )
        })()}

        {/* Coordinator slot list — every live server-side position, read-only
            plus a per-slot stop. Click a row to chart that token; the Pip-Boy
            above follows the charted token's slot automatically. */}
        {mode === 'server' && (() => {
          const liveSlots = (bot.getSlots?.() || []).filter(sl => sl.token)
          if (!liveSlots.length) return null
          const maxBots = status.coordinatorCfg?.maxBots
          return (
            <div className="bot-menu-positions-list bot-slot-list">
              <div className="bot-menu-positions-title">
                Slots ({liveSlots.length}{maxBots ? `/${maxBots}` : ''})
              </div>
              {liveSlots.map(sl => {
                const t = String(sl.token)
                const label = t.length > 8 ? `${t.slice(0, 4)}…${t.slice(-4)}` : t
                // Live unrealized from the slot's own status; the slot's
                // realized daily only when it has no open position.
                const _slLive = openPnl(sl.status)
                const pnl = _slLive !== null ? _slLive : (sl.dailyPnL || 0)
                return (
                  <div
                    key={sl.slot}
                    className="bot-menu-positions-item"
                    onClick={() => chartToken(sl.token)}
                    title={`Slot ${sl.slot} · ${sl.source === 'auto' ? 'discovery pick' : 'manual watch'} — click to chart`}
                  >
                    <span className="bot-menu-positions-side">
                      {sl.source === 'auto' ? '🔎' : '📌'}
                    </span>
                    <span className="bot-menu-positions-mint">{label}</span>
                    <span className="bot-menu-positions-detail">
                      {sl.state}
                      {' · '}
                      <span style={{ color: pnl >= 0 ? '#66e09f' : '#ff7878' }}>
                        {(pnl >= 0 ? '+' : '') + (pnl * 100).toFixed(1)}%
                      </span>
                    </span>
                    <button
                      className="bot-slot-stop"
                      title="Stop this slot (release + token cooldown)"
                      onClick={(e) => { e.stopPropagation(); bot.stopSlot?.(sl.token) }}
                    >✕</button>
                  </div>
                )
              })}
            </div>
          )
        })()}
      </div>

      {showJournal ? (
        <div className="bot-journal">
          {(() => {
            const entries = bot.getJournal ? bot.getJournal() : []
            if (!entries.length) {
              return <div className="bot-journal-empty">No closed trades yet.</div>
            }
            const wins = entries.filter(e => (e.exit?.pnlPct ?? 0) > 0).length
            const sumR = entries.reduce((a, e) => a + reportR(e.exit), 0)
            return (
              <>
                <div className="bot-journal-summary">
                  <span>{entries.length} trades</span>
                  <span>{Math.round((wins / entries.length) * 100)}% win</span>
                  <span className={sumR >= 0 ? 'pos' : 'neg'}>{nf(sumR, 1)}R total</span>
                </div>
                <div className="bot-journal-tools">
                  <button className="bjt-btn" onClick={() => openBotJournalReport(entries)} title="Open full expectancy report in a new tab">📊 Report</button>
                  <button className="bjt-btn" onClick={() => exportBotJournal(entries)} title="Download journal as JSON">⬇ Export</button>
                  {/* Clears BOTH brains' stores: local OrderflowBot wipes its
                      localStorage key; RemoteBot sends {cmd:'resetJournal'} and
                      the runner clears every slot + the merged store file, so
                      no in-memory copy resurrects it. Export first — this is
                      the only copy of the trade history. */}
                  <button className="bjt-btn" style={{ color: '#ff5252', borderColor: '#5a2a2a' }}
                    onClick={() => {
                      if (!window.confirm(`Clear ${entries.length} bot trade record(s)? Export first if you want to keep them — this cannot be undone.`)) return
                      const n = bot.resetJournal ? bot.resetJournal() : 0
                      console.log(`[bot.journal] cleared ${n} entries`)
                    }}
                    title="Permanently clear the bot trade journal (server store included in server mode)">🗑 Clear</button>
                </div>
                <div className="bot-journal-list">
                  {entries.map((e, i) => {
                    const px = e.exit?.pnlPct ?? 0
                    const r = reportR(e.exit)
                    const win = px > 0
                    const dur = e.durationMs ? Math.round(e.durationMs / 60000) : 0
                    const dir = e.direction || e.pilotEntry?.direction || ''
                    // The mint IS the identity of a memecoin trade — neither
                    // brain writes `symbol`, so every row used to read as four
                    // bare characters with nothing to click. Show it the way
                    // the slot list does (ABCD…WXYZ) and make the row navigate.
                    // Untagged rows — Hyperliquid trades, pre-tagging history —
                    // have no address to go to, so they stay inert and fall
                    // back to whatever label they carry.
                    const mint = e.token ? String(e.token) : ''
                    const short = mint.length > 8 ? `${mint.slice(0, 4)}…${mint.slice(-4)}` : mint
                    return (
                      <div
                        key={e.id || i}
                        className={`bot-journal-row ${win ? 'win' : 'loss'}${mint ? ' clickable' : ''}`}
                        onClick={mint ? () => chartToken(mint) : undefined}
                        title={mint ? `${mint}\nclick to chart` : undefined}
                      >
                        <div className="bjr-top">
                          <span className={`bjr-sym${mint ? ' link' : ''}`}>{short || e.symbol || '?'}</span>
                          {dir && <span className={`bjr-dir ${dir.toLowerCase()}`}>{dir}</span>}
                          {mint && (
                            <button
                              className={`bjr-copy ${copiedMint === mint ? 'copied' : ''}`}
                              title="Copy full mint address"
                              onClick={(ev) => { ev.stopPropagation(); flashCopy(mint) }}
                            >{copiedMint === mint ? '✓' : '⧉'}</button>
                          )}
                          <span className={`bjr-pnl ${win ? 'pos' : 'neg'}`}>{win ? '+' : ''}{nf(px * 100, 1)}%</span>
                          <span className={`bjr-r ${r >= 0 ? 'pos' : 'neg'}`}>{nf(r, 1)}R</span>
                        </div>
                        <div className="bjr-bot">
                          <span>{Math.round(e.pilotEntry?.mcap || e.adoption?.mcap || 0).toLocaleString()} → {Math.round(e.exit?.mcap || 0).toLocaleString()}</span>
                          <span className="bjr-reason">{e.exit?.reason || ''}</span>
                          {dur > 0 && <span className="bjr-dur">{dur}m</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )
          })()}
        </div>
      ) : (
      <>

      {/* Auto-pilot error / status banner — shown briefly on refusal or timeout */}
      {autoPilotError && (
        <div className="bot-menu-autopilot-err">
          {autoPilotError}
        </div>
      )}


      {/* ─── Server bot (☁️): execution + analysis, then the hunt knobs ─── */}
      {mode === 'server' && (<>
        {secHead('server', 'Server', () => `${status.dryRun ? 'DRY' : 'LIVE'} · ${fmtBars(status.intervalSec || 5)}`)}
        {secOpen.server && (
        <div className="bot-menu-section">
          {onDryRunToggle && (
            <div className="bot-menu-row">
              <span className="bot-menu-label">Execution</span>
              <button
                className={`bot-menu-btn dryrun-btn ${status.dryRun ? '' : 'live'}`}
                style={{ minWidth: 92 }}
                disabled={bot.online === false}
                onClick={() => onDryRunToggle(!status.dryRun)}
                title={status.dryRun
                  ? 'DRY RUN — signals journal on paper, nothing executes. Click to go LIVE.'
                  : 'LIVE — exits execute with real funds. Click for dry run.'}
              >
                {status.dryRun ? '🧪 DRY' : '💰 LIVE'}
              </button>
            </div>
          )}
          {onAnalysisChange && (
            <div className="bot-menu-row">
              <span className="bot-menu-label">Bars</span>
              <TypeLine
                value={status.intervalSec || 5}
                fmt={fmtBars}
                parse={barsIn}
                opts={BAR_SECONDS}
                onCommit={(v) => onAnalysisChange({ intervalSec: v })}
              />
            </div>
          )}
        </div>
        )}
      </>)}

      {/* ─── Foundations (entry-gates-20261018) ───
          THE entry logic (entry-gates2-20261018: replaced the old Entry
          section). Six structural rules + one timing switch, each its own
          ON/OFF (OrderflowBot ctx* config, default ON), then two guards.
          Skips log as LIQ_BASE_* / NOT_LOW_IN_VALUE / VALUE_NA /
          PRIOR_LOW_LOST / NO_DELTA_UP / NO_ABSORB_CELL /
          ABSORB_NO_REACTION / DOWNTREND / TREND_NA. */}
      {secHead('foundation', 'Entry · Foundations', () => {
        const on = FOUNDATION_FLAGS.filter(([, k]) => cfg[k] === true).length
        return `${on}/6 on${cfg.ctxFast === true ? ' · fast' : ''}`
      })}
      {secOpen.foundation && (
      <div className="bot-menu-section">
        {flagRows(FOUNDATION_FLAGS.filter(([, k]) => k !== 'ctxVwap'))}
        {/* 6 VWAP + Max ext in one row (optclean-20261026): off = ctxVwap
            false; a number = ctxVwap true + maxExtAtr. maxExtAtr is only
            read when ctxVwap is on, so one control says everything. */}
        <div className="bot-menu-row" title={FOUNDATION_FLAGS.find(([, k]) => k === 'ctxVwap')[2]}>
          <span className="bot-menu-label">6 VWAP ext ≤</span>
          <TypeLine
            value={cfg.ctxVwap === true && cfg.maxExtAtr != null ? cfg.maxExtAtr : OFF}
            fmt={fmtOff(fmtAtr)} parse={offOr(posNum)} opts={withOff([1.5, 2.0, 2.5, 3.0])}
            onCommit={(v) => apply(v === OFF ? { ctxVwap: false } : { ctxVwap: true, maxExtAtr: v })}
          />
        </div>
        {knobRows([
          ['Liq window',  'ctxLiqBaseMin',   [30, 45, 60, 90],        fmtOff(v => `${+v}m`),          t => { const n = intIn(t); return n !== null && n >= 15 && n <= 240 ? n : null },
            'Liquidity: rolling window the base and value area are measured over. The droplet keeps 60 min of 15s bars, so 90 needs maxCandles raised in bot_runner.'],
          ['Liq buy ≥',   'ctxLiqBuyMin',    [0.40, 0.42, 0.45, 0.50],      fmtOff(fmtPct1),       pctIn,
            'Liquidity: buy share of the window volume must be at least this (two-sided, not a sell-off)'],
          ['Liq gap ≤',   'ctxLiqMaxGapSec', [30, 60, 90, 120],       fmtOff(v => `${+v}s`),          t => { const n = intIn(t); return n !== null && n >= 5 ? n : null },
            'Liquidity: longest stretch with no trades allowed inside the window'],
          ['Value tol',   'ctxValueTol',     [0.10, 0.20, 0.35, 0.50],   fmtOff(fmtPct1),       pctIn,
            'Value: enter at or below value-area low + this share of the value-area width'],
          ['Absorb within','ctxAbsorbSec',   [60, 120, 180, 300],     fmtOff(v => `${+v}s`),          t => { const n = intIn(t); return n !== null && n >= 15 ? n : null },
            'Absorption cells: the cell must be this recent'],
          ['Absorb ×',    'ctxAbsorbMult',   [2, 3, 4, 5],            fmtOff(v => `${+v}×`),          posNum,
            'Absorption cells: cell sell volume ≥ this × the median cell (default 2×)'],
          ['Lower highs ≥','ctxTrendLH',     [3, 4, 5],               fmtOff(v => `${+v}/5`),         t => { const n = intIn(t); return n !== null && n >= 1 && n <= 5 ? n : null },
            'Trend: veto when this many of the last 5 steps between six 5-min highs are lower highs'],
        ])}
        {flagRows(TIMING_FLAGS)}
        {knobRows([
          ['Max up bars', 'maxUpBars',   withOff([4, 5, 6, 8]),        fmtOff(v => String(v)), offOr(t => { const n = intIn(t); return n !== null && n >= 1 ? n : null }),
            'Skip after more than this many consecutive up bars (1m)'],
        ])}
      </div>
      )}

      {/* ─── Market (2026-10-18-market) ───
          Broad-market health + hot theme. Both default OFF. Data comes from
          bot_runner (Jupiter, every 60s, only while one is on); the browser
          bot has no feed, so local mode skips with MARKET_NA / THEME_NA. */}
      {secHead('market', 'Market', () => {
        const on = MARKET_FLAGS.filter(([, k]) => cfg[k] === true).map(([l]) => l.split(' ')[0])
        return on.length ? on.join(' · ') : 'off'
      })}
      {secOpen.market && (
      <div className="bot-menu-section">
        {flagRows(MARKET_FLAGS)}
        {knobRows([
          ['SOL 1h ≥',    'mktSol1hMin',   [-1, -0.5, 0, 0.5],       fmtOff(v => `${+v}%`),  numPlain,
            'Market filter: SOL price change over the last hour must be at least this (percent)'],
          ['SOL 6h ≥',    'mktSol6hMin',   [-3, -2, -1, 0],          fmtOff(v => `${+v}%`),  numPlain,
            'Market filter: SOL price change over the last 6 hours must be at least this (percent)'],
          ['Breadth ≥',   'mktBreadthMin', [0.30, 0.40, 0.50, 0.60], fmtOff(fmtPct1),        pctIn,
            'Market filter: share of Jupiter trending + top-traded (1h) coins that are up over the last hour'],
          ['Theme ≥',     'mktThemeMin',   [1, 2, 3, 4],             fmtOff(v => `${+v} coins`), t => { const n = intIn(t); return n !== null && n >= 1 ? n : null },
            'Hot theme: at least this many OTHER recently trending coins must share this coin\'s theme'],
        ])}
      </div>
      )}

      {/* ─── Sizing ─── */}
      {secHead('sizing', 'Sizing', () => `${starterSize.toFixed(2)} SOL${cfg.riskPct != null ? ` · risk ${fmtPct1(cfg.riskPct)}` : ''}`)}
      {secOpen.sizing && (
      <div className="bot-menu-section">

        <div className="bot-menu-row">
          <span className="bot-menu-label" title="Risk base: SOL risked per trade = this × Risk. Not the buy size — size = risk ÷ stop distance, clamped to Size min/max (this is also the fallback size if the stop distance is 0).">Risk base</span>
          <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'starter' ? null : 'starter')}>
            {starterSize.toFixed(2)} SOL
            <Dropdown open={openMenu === 'starter'}>
              {STARTER_SIZES.map(v => (
                <div key={v} onClick={() => { setStarterSize(v); apply({ starterSizeSol: v }); setOpenMenu(null) }}>
                  {v.toFixed(2)} SOL
                </div>
              ))}
            </Dropdown>
          </div>
        </div>

        <div className="bot-menu-row">
          <span className="bot-menu-label">Load (add)</span>
          <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'load' ? null : 'load')}>
            {loadSize.toFixed(2)} SOL
            <Dropdown open={openMenu === 'load'}>
              {LOAD_SIZES.map(v => (
                <div key={v} onClick={() => { setLoadSize(v); apply({ loadSizeSol: v }); setOpenMenu(null) }}>
                  {v.toFixed(2)} SOL
                </div>
              ))}
            </Dropdown>
          </div>
        </div>

        {/* Risk-based sizing: each pilot risks Starter × Risk SOL; the size
            is that risk ÷ the stop distance, clamped to Size min/max. */}
        {knobRows([
          ['Risk %',   'riskPct',    [0.02, 0.03, 0.04, 0.05, 0.08], v => `${fmtPct1(v)} · ${+((cfg.starterSizeSol || 0) * v).toFixed(4)}`, pctIn,
            'SOL risked per trade = Risk base × this (shown after the dot). Position size = risk ÷ stop distance.'],
          ['Size min', 'minSizeSol', [0.005, 0.01, 0.02, 0.05],     v => `${+v} SOL`, posNum, 'Floor: smallest pilot the risk sizing may buy'],
          ['Size max', 'maxSizeSol', withOff([0.05, 0.10, 0.25, 0.50]), fmtOff(v => `${+v} SOL`), offOr(posNum), 'Cap: largest pilot the risk sizing may buy (off = no cap)'],
        ])}
      </div>
      )}

      {/* ─── Pilot ─── */}
      {secHead('pilot', 'Pilot', () => `${fmtStop(pilotStop)}${pilotStop === 'atr' ? ` · ${levelAtrMult.toFixed(1)}×` : ''}`)}
      {secOpen.pilot && (
      <div className="bot-menu-section">

        <div className="bot-menu-row">
          <span className="bot-menu-label">Stop</span>
          <TypeLine
            value={pilotStop}
            fmt={fmtStop}
            parse={stopIn}
            opts={[...STOP_MODES, ...STOP_PCTS]}
            onCommit={(v) => {
              setPilotStop(v)
              // 'vwap' / 'atr' hand the exit to a source rather than a number.
              // pilotStopPct stays numeric so nothing downstream reads a
              // string as a multiplier; the mode rides alongside it.
              apply(typeof v === 'string'
                ? { pilotStopMode: v }
                : { pilotStopMode: 'pct', pilotStopPct: v })
            }}
          />
        </div>

        <div className="bot-menu-row">
          <span className="bot-menu-label">Max hold</span>
          <TypeLine
            value={pilotHold}
            fmt={fmtHold}
            parse={holdIn}
            opts={HOLD_OPTS}
            onCommit={(v) => {
              setPilotHold(v)
              apply(typeof v === 'string'
                ? { pilotHoldMode: v }
                : { pilotHoldMode: 'time', pilotMaxHoldMin: v })
            }}
          />
        </div>

        {/* ENTRY STOP — levelAtrMult. Used by Stop = ATR: initial stop =
            entry − mult × ATR (mgmt bars, ATR period from the Loaded trail).
            Distinct from the trail multipliers. This one sets the INITIAL stop: entry − mult × ATR. It had no control
            at all, which mattered once bars went 60s → 15s: ATR then spans
            3.5 min instead of 14, so the same 1.5 multiplier became a stop
            inside ordinary tick noise and positions died seconds after
            opening. Widening this is the fix for that, and it was only
            reachable by hand-sending a botConfig command. */}
        <div className="bot-menu-row">
          <span className="bot-menu-label">Entry stop ×</span>
          <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'lvlatr' ? null : 'lvlatr')}>
            {levelAtrMult.toFixed(1)}
            <Dropdown open={openMenu === 'lvlatr'}>
              {ATR_MULTS.map(v => (
                <div key={v} onClick={() => { setLevelAtrMult(v); apply({ levelAtrMult: v }); setOpenMenu(null) }}>
                  {v.toFixed(1)}
                </div>
              ))}
            </Dropdown>
          </div>
        </div>

        {/* Stop buffer is only read by BASE / SWING stops — hidden otherwise
            (value kept). Stop % lives in the Stop picker and only applies in
            pct mode (and as the last-resort fallback). */}
        {(pilotStop === 'base' || pilotStop === 'swing') && knobRows([
          ['Stop buffer', 'stopBufferPct', [0, 0.005, 0.01, 0.02],       fmtPct1,         pctIn,
            'BASE / SWING stops sit this far under the level'],
        ])}
        {knobRows([
          ['Max stop',    'maxStopPct',    withOff([0.15, 0.25, 0.35]),  fmtOff(fmtPct1), offOr(pctIn),
            'Skip the entry if the stop would be wider than this'],
        ])}

      </div>
      )}

      {/* ─── Management (ATR trail, lock ladder, scale-out, trail start, runner giveback) ─── */}
      {secHead('management', 'Management', () => `trail ${loadAtrP}/${loadAtrH}/${atrMult.toFixed(1)} · lock ${fmtLadder(cfg.lockSteps)}`)}
      {secOpen.management && (
      <div className="bot-menu-section">
        {/* ATR trail — ONE row for pilot + loaded (optclean-20261026). Live
            pilot and loaded trails were identical (5/10/2.0), so a pick here
            writes both stages' keys. atrPeriod also sets the ATR used by
            Stop = ATR / Entry stop ×, same as the old Loaded row did. If the
            stages ever differ (hand-sent config) the label shows "≠" and the
            row displays the loaded values. Runner trail × stays config-only. */}
        <div className="bot-menu-row" title={trailSplit ? `Pilot ${pilotAtrP}/${pilotAtrH}/${pilotAtrM} differs from loaded — picking a value sets both` : 'ATR trail for pilot and loaded positions'}>
          <span className="bot-menu-label">ATR trail{trailSplit ? ' ≠' : ''}</span>
          <div className="bot-menu-atr3">
            <div className="bot-menu-select"
                 title="ATR period — bars of true range in the average"
                 onClick={() => setOpenMenu(openMenu === 'lAtrP' ? null : 'lAtrP')}>
              {loadAtrP}
              <Dropdown open={openMenu === 'lAtrP'}>
                {ATR_PERIODS.map(v => (
                  <div key={v} onClick={() => { setLoadAtrP(v); setPilotAtrP(v); apply({ atrPeriod: v, pilotAtrPeriod: v }); setOpenMenu(null) }}>
                    {v}
                  </div>
                ))}
              </Dropdown>
            </div>
            <span className="atr3-sep">/</span>
            <div className="bot-menu-select"
                 title="HHV period — bars the stop line ratchets over"
                 onClick={() => setOpenMenu(openMenu === 'lAtrH' ? null : 'lAtrH')}>
              {loadAtrH}
              <Dropdown open={openMenu === 'lAtrH'}>
                {ATR_HHVS.map(v => (
                  <div key={v} onClick={() => { setLoadAtrH(v); setPilotAtrH(v); apply({ atrHhvPeriod: v, pilotAtrHhvPeriod: v }); setOpenMenu(null) }}>
                    {v}
                  </div>
                ))}
              </Dropdown>
            </div>
            <span className="atr3-sep">/</span>
            <div className="bot-menu-select"
                 title="Multiplier — high minus mult x ATR"
                 onClick={() => setOpenMenu(openMenu === 'atr' ? null : 'atr')}>
              {atrMult.toFixed(1)}
              <Dropdown open={openMenu === 'atr'}>
                {ATR_TRAIL_MULTS.map(v => (
                  <div key={v} onClick={() => { setAtrMult(v); setPilotAtrM(v); apply({ loadAtrMult: v, pilotAtrMult: v }); setOpenMenu(null) }}>
                    {v.toFixed(1)}
                  </div>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
        {knobRows([
          ['Lock ladder', 'lockSteps',   LADDER_PRESETS,              fmtLadder,       ladderIn,
            'At +trigger R move the stop to the lock (BE = entry + fee buffer). Type e.g. "1>be 2>1 3>2", or off.'],
          ['Fee buffer',  'feeBuffer',   [0, 0.005, 0.01, 0.02],      fmtPct1,         pctIn,
            'Breakeven lock sits this far above entry to cover fees'],
          ['Scale out',   'scaleOutPct', withOff([0.25, 0.33, 0.5, 0.75]), fmtOff(fmtPct1), offOr(pctIn),
            'Sell this share of the position once at Scale at'],
          ['Scale at',    'scaleOutR',   [1, 1.5, 2, 3],              fmtR,            posNum,
            'R multiple that triggers the scale-out'],
          ['Trail start', 'trailStartR', withOff([0.5, 1, 1.5, 2]),   v => v === OFF || v == null ? 'entry' : fmtR(v), offOr(posNum),
            'ATR trail arms only after this peak R (off = from entry)'],
          ['Runner GB',   'runnerGivebackPct', withOff([0.3, 0.4, 0.5, 0.6]), fmtOff(fmtPct1), offOr(pctIn),
            'Runners (2.5R+) exit after giving back this share of peak R until the ATR trail is above the stop'],
        ])}
      </div>
      )}

      {/* ─── Advanced (risk rails) ─── */}
      {secHead('risk', 'Risk rails', () => `loss ${dailyLoss === OFF || dailyLoss == null ? 'off' : `${(dailyLoss * 100).toFixed(0)}%`} · ${maxTrades === OFF || maxTrades == null ? 'off' : maxTrades}/day`)}

      {secOpen.risk && (
        <div className="bot-menu-section">
          <div className="bot-menu-row">
            <span className="bot-menu-label">Daily loss cap</span>
            <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'dailyLoss' ? null : 'dailyLoss')}>
              {dailyLoss === OFF || dailyLoss == null ? 'off' : `${(dailyLoss * 100).toFixed(0)}%`}
              <Dropdown open={openMenu === 'dailyLoss'}>
                {DAILY_LOSS_PCT.map(v => (
                  <div key={v} onClick={() => {
                    setDailyLoss(v); setOpenMenu(null)
                    // null, not 0: a 0% cap would halt on the first tick.
                    apply({ maxDailyLossPct: v === OFF ? null : v })
                  }}>{v === OFF ? 'off' : `${(v * 100).toFixed(0)}%`}</div>
                ))}
              </Dropdown>
            </div>
          </div>

          <div className="bot-menu-row">
            <span className="bot-menu-label">Max trades/day</span>
            <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'maxTrades' ? null : 'maxTrades')}>
              {maxTrades === OFF || maxTrades == null ? 'off' : maxTrades}
              <Dropdown open={openMenu === 'maxTrades'}>
                {MAX_TRADES_DAY.map(v => (
                  <div key={v} onClick={() => {
                    setMaxTrades(v); setOpenMenu(null)
                    apply({ maxTradesPerDay: v === OFF ? null : v })
                  }}>{v === OFF ? 'off' : v}</div>
                ))}
              </Dropdown>
            </div>
          </div>

          <div className="bot-menu-row">
            <span className="bot-menu-label">Cooldown</span>
            <div className="bot-menu-select" onClick={() => setOpenMenu(openMenu === 'cooldown' ? null : 'cooldown')}>
              {cooldownS === OFF || !cooldownS ? 'off' : `${cooldownS}s`}
              <Dropdown open={openMenu === 'cooldown'}>
                {COOLDOWN_S.map(v => (
                  <div key={v} onClick={() => {
                    setCooldownS(v); setOpenMenu(null)
                    apply({ cooldownMs: v === OFF ? 0 : v * 1000 })
                  }}>{v === OFF ? 'off' : `${v}s`}</div>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
      )}

      {/* ─── Discovery (server hunt) ─── */}
      {mode === 'server' && onDiscoverySettings && (
        <>
          {secHead('discovery', 'Discovery', () => `hunt ${status.discovery ? 'ON' : 'OFF'} · ${String(dcfg.source || 'trending')}${dcfg.pumpOnly ? ' · pump only' : ''}`)}
          {secOpen.discovery && (
            <div className="bot-menu-section">
              {onDiscoverToggle && (
                <div className="bot-menu-row">
                  <span className="bot-menu-label">Hunt</span>
                  <button
                    className={`bot-menu-btn discover-btn ${status.discovery ? 'on' : ''}`}
                    style={{ minWidth: 92 }}
                    disabled={bot.online === false}
                    onClick={() => onDiscoverToggle(!status.discovery)}
                  >
                    {status.discovery ? '🔎 ON' : '🔍 OFF'}
                  </button>
                </div>
              )}
              {/* optclean-20261026: the everyday filters stay visible; the rest
                  sit under a collapsed Advanced (remembered like the other
                  sections). Hidden values keep working on the server. */}
              {discRows([
                // [label, key, current, presets, fmt, parse]
                // ── Source (2026-10-18-discovery): TRENDING = movers on the
                //    Jupiter trending list; BASING = two-sided ranges before the
                //    move (Base * rows); BOTH = alternate the two lanes.
                ['Source',      'source',           dcfg.source ?? 'trending',    ['trending', 'basing', 'both'],          v => String(v).toUpperCase(), t => { const v = String(t).trim().toLowerCase(); return ['trending', 'basing', 'both'].includes(v) ? v : null }],
                ['Mcap min',    'minMcapUsd',       dcfg.minMcapUsd ?? 150000,    [50000, 100000, 150000, 300000],         fmtUsd,                       numIn],
                ['Mcap max',    'maxMcapUsd',       dcfg.maxMcapUsd ?? 30000000,  [5000000, 10000000, 30000000, 100000000], fmtUsd,                      numIn],
                ['Min liq',     'minLiquidity',     dcfg.minLiquidity ?? 25000,   [10000, 25000, 50000, 100000],           fmtUsd,                       numIn],
                ['Min volume',  'minVolumeUsd',     dcfg.minVolumeUsd ?? 50000,   [10000, 25000, 50000, 100000],           fmtUsd,                       numIn],
              ])}
              <div className="bot-menu-row">
                <span className="bot-menu-label">Pump only</span>
                <button
                  className={`bot-menu-btn discover-btn ${dcfg.pumpOnly ? 'on' : ''}`}
                  style={{ minWidth: 92 }}
                  disabled={bot.online === false}
                  onClick={() => onDiscoverySettings({ pumpOnly: !dcfg.pumpOnly })}
                >
                  {dcfg.pumpOnly ? 'ON' : 'OFF'}
                </button>
              </div>
              {secHead('discoveryAdv', 'Advanced', () => `21 more filters`)}
              {secOpen.discoveryAdv && discRows([
                // ── Scan cadence + windows ──
                ['Trend win',   'trendInterval',    dcfg.trendInterval ?? '5m',   ['5m', '1h', '6h', '24h'],               v => v,                       winIn],
                ['Stats win',   'statsWindow',      dcfg.statsWindow ?? '5m',     ['5m', '1h', '6h', '24h'],               v => v,                       winIn],
                ['Poll',        'pollSec',          dcfg.pollSec ?? 60,           [30, 60, 120, 300],                      v => v + 's',                 t => { const n = intIn(t); return n !== null && n >= 15 ? n : null }],
                // ── Safety / distribution ──
                ['Whale max',   'maxTopHolderPct',  dcfg.maxTopHolderPct ?? 45,   [25, 35, 45, 60],                        v => v + '%',                 t => { const n = intIn(t); return n !== null && n >= 1 && n <= 100 ? n : null }],
                ['Min holders', 'minHolders',       dcfg.minHolders ?? 300,       [100, 200, 300, 500],                    v => String(v),               intIn],
                ['Organic min', 'minOrganicScore',  dcfg.minOrganicScore ?? 40,   [0, 20, 40, 60],                         v => String(v),               t => { const n = intIn(t); return n !== null && n <= 100 ? n : null }],
                // ── Trend + volume gates ──
                ['Min trades',  'minTrades',        dcfg.minTrades ?? 150,        [50, 150, 300, 600],                     v => String(v),               intIn],
                ['Min move',    'minAbsPriceChange', dcfg.minAbsPriceChange ?? 0.05, [0.02, 0.05, 0.08, 0.12],             v => Math.round(v * 100) + '%', pctIn],
                ['Turnover',    'minVolPerMcap',    dcfg.minVolPerMcap ?? 0.15,   [0.05, 0.15, 0.3, 0.5],                  v => Math.round(v * 100) + '%', pctIn],
                // ── Basing lane (Source BASING / BOTH) — 1h stats ──
                ['Base vol 1h', 'baseMinVol1hUsd',  dcfg.baseMinVol1hUsd ?? 100000, [50000, 100000, 250000, 500000],       fmtUsd,                       numIn],
                ['Base trades', 'baseMinTrades1h',  dcfg.baseMinTrades1h ?? 600,  [300, 600, 1200, 2500],                  v => v + '/1h',               intIn],
                ['Base 1h ≤',   'baseMaxChg1h',     dcfg.baseMaxChg1h ?? 0.15,    [0.05, 0.10, 0.15, 0.25],                v => Math.round(v * 100) + '%', pctIn],
                ['Base 5m ≤',   'baseMaxChg5m',     dcfg.baseMaxChg5m ?? 0.05,    [0.02, 0.03, 0.05, 0.08],                v => Math.round(v * 100) + '%', pctIn],
                ['Base buys ≥', 'baseBuyMin',       dcfg.baseBuyMin ?? 0.45,      [0.40, 0.45, 0.48],                      v => Math.round(v * 100) + '%', pctIn],
                ['Base buys ≤', 'baseBuyMax',       dcfg.baseBuyMax ?? 0.55,      [0.52, 0.55, 0.60],                      v => Math.round(v * 100) + '%', pctIn],
                ['Base alive',  'baseMinTrades5m',  dcfg.baseMinTrades5m ?? 30,   [10, 30, 60, 120],                       v => v + '/5m',               intIn],
                // ── Rotation ──
                ['Rotate',      'rotateAfterMin',   dcfg.rotateAfterMin ?? 75,    [30, 45, 75, 90],                        v => v + 'm',                 t => { const n = intIn(t); return n !== null && n >= 1 ? n : null }],
                ['Dead tape',   'deadTapeAfterSec', dcfg.deadTapeAfterSec ?? 120, [60, 120, 180, 300],                     v => v + 's',                 t => { const n = intIn(t); return n !== null && n >= 10 ? n : null }],
                ['Dead prints', 'deadTapeMin',      dcfg.deadTapeMin ?? 10,       [5, 10, 20, 40],                         v => '<' + v,                 intIn],
                ['Base pin',    'basePinMaxSec',    dcfg.basePinMaxSec ?? 480,    [300, 480, 720, 900],                    v => Math.round(v / 60) + 'm', t => { const n = intIn(t); return n === null ? null : n * 60 }],
                ['Cooldown',    'cooldownMin',      dcfg.cooldownMin ?? 45,       [15, 30, 45, 90],                        v => v + 'm',                 intIn],
              ])}
            </div>
          )}
        </>
      )}

      {/* Reset */}
      <div style={{ borderTop: '1px solid #2a2a2a', marginTop: 6, paddingTop: 6 }}>
        <button
          className="bot-menu-btn"
          style={{ width: '100%', color: '#ff7878', borderColor: '#552222' }}
          onClick={() => { onReset(); if (!pinned) onClose() }}
        >
          Reset stats
        </button>
      </div>
      </>
      )}
    </div>,
    document.body
  )
}

// ============================================================
// BotBadge — DOM HUD overlay
// ============================================================

const STATE_COLORS = {
  [STATES.SCANNING]: '#888888',
  [STATES.PILOT]:    '#ffb300',
  [STATES.LOADED]:   '#00c853',
  [STATES.COOLDOWN]: '#616161',
}

export function BotBadge({ bot, waiting, walletConnected, onContextMenu, dataSource, hlPair }) {
  const isFutures = dataSource === 'hyperliquid' && hlPair
  const fmtVal = isFutures
    ? (v) => {
        if (v >= 10000) return '$' + Math.round(v).toLocaleString()
        if (v >= 1) return '$' + v.toFixed(2)
        return '$' + v.toPrecision(4)
      }
    : (v) => (v / 1000).toFixed(0) + 'K'

  // Icon-only mode (click the robot head). Persisted. While compact, a
  // brief readout flashes when the state / trade count changes, and a
  // link appears when discovery picks a new coin.
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('solcheddar-botbadge-compact') === '1' } catch { return false }
  })
  const toggleCompact = (e) => {
    e.stopPropagation()
    setCompact(c => {
      const n = !c
      try { localStorage.setItem('solcheddar-botbadge-compact', n ? '1' : '0') } catch {}
      return n
    })
  }
  const [flashes, setFlashes] = useState([])   // [{ text, key }] stacked lines (max 3)
  const [found, setFound] = useState(null)   // mint address
  const seenRef = useRef(null)
  const _bs = bot?.getStatus?.()
  // stream-all-20261023: every field the full badge shows is diffed; any new
  // value is queued as a short message beside the icon. First values seed
  // silently. Live P/L is throttled (>=0.5% move or 10s since last emit).
  const _short = (t) => { const x = String(t); return x.length > 10 ? x.slice(0, 4) + '…' : x }
  const _fields = {}
  if (bot) {
    const off = !bot.enabled
    _fields.state = off ? 'OFF' : (_bs?.state || '')
    if (!off) {
      _fields.trades = `T:${_bs?.dailyTradeCount ?? 0}`
      _fields.wallet = waiting ? 'waiting for wallet' : !walletConnected ? 'wallet locked' : 'wallet ok'
      if (_bs?.currentToken) _fields.coin = `watching ${_short(_bs.currentToken)}`
      const ab = _bs?.activeBase
      _fields.base = ab ? `base ${Math.floor((ab.durationSec || 0) / 60)}m · abs ${nf(ab.absorption, 1)}×${ab.qualified ? ' · READY' : ''}`
        : (_bs?.state === STATES.SCANNING ? 'scanning · no base' : '')
      if (_bs?.state === STATES.PILOT && _bs?.pilot) _fields.pilot = `PILOT @ ${fmtVal(_bs.pilot.mcap)}`
      if (_bs?.state === STATES.LOADED && _bs?.load) {
        _fields.load = `${_bs.load.adopted ? 'AUTO' : 'LOADED'} basis ${fmtVal(_bs.load.blendedBasis)}`
        _fields.peakR = `peak ${nf(Math.floor((_bs.load.peakR || 0) * 2) / 2, 1)}R`
      }
      const lr = _bs?.liqReaction
      if (lr) {
        const ar = (sd) => sd === 'low' ? '↓' : '↑'
        _fields.liq = lr.pending ? `liq ${ar(lr.pending.side)}swept`
          : lr.last ? `liq ${ar(lr.last.side)}${lr.last.sec == null ? 'hold' : nf(lr.last.sec, 0) + 's'}` : ''
        if (lr.medianSec != null) _fields.liqMd = `liq md ${nf(lr.medianSec, 0)}s`
      }
    }
  }
  const _fieldsKey = JSON.stringify(_fields)
  let _pnlNum = null
  if (bot && _bs) {
    const op = openPnl(_bs)
    const inPos = _bs.state === STATES.PILOT || _bs.state === STATES.LOADED
    _pnlNum = (inPos && op !== null) ? op : _bs.dailyPnL
  }
  const prevFieldsRef = useRef(null)
  const pnlRef = useRef({ v: null, t: 0 })
  // bot-stream-stack-20261023: messages arriving within ~300ms of each other
  // show together as stacked lines (max 3), each on its own 2.5s timer.
  // Overflow / later messages queue (cap 5) and fill a slot as one expires.
  // bot-stream-ontop-20261023: one message types out at a time (>=700ms
  // apart); the newest sits on top and older lines slide down. Each line
  // lives 5s; at most 3 visible (oldest dropped). Queue cap 5.
  const queueRef = useRef([])
  const busyRef = useRef(false)
  const keyRef = useRef(0)
  const pump = () => {
    const text = queueRef.current.shift()
    if (text == null) { busyRef.current = false; return }
    busyRef.current = true
    const key = ++keyRef.current
    setFlashes(f => [{ text, key }, ...f].slice(0, 3))
    setTimeout(() => setFlashes(f => f.filter(x => x.key !== key)), 5000)
    setTimeout(pump, 700)
  }
  const enqueue = (text) => {
    const q = queueRef.current
    q.push(text)
    while (q.length > 5) q.shift()   // drop oldest
    if (!busyRef.current) pump()
  }
  useEffect(() => {
    const prev = prevFieldsRef.current
    prevFieldsRef.current = _fields
    if (!prev) return
    if (!compact) return
    for (const k of Object.keys(_fields)) {
      const v = _fields[k]
      if (v && v !== prev[k]) enqueue(v)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_fieldsKey, compact])
  useEffect(() => {
    if (_pnlNum == null || !Number.isFinite(_pnlNum)) return
    const r = pnlRef.current, now = Date.now()
    if (r.v == null) { pnlRef.current = { v: _pnlNum, t: now }; return }
    const moved = Math.abs(_pnlNum - r.v) * 100
    if (moved >= 0.5 || (moved >= 0.05 && now - r.t >= 10000)) {
      pnlRef.current = { v: _pnlNum, t: now }
      if (compact) enqueue(`P/L ${_pnlNum >= 0 ? '+' : ''}${nf(_pnlNum * 100, 1)}%`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_pnlNum, compact])
  const _autoTokens = [
    ...(bot?.getSlots?.() || []).filter(sl => sl.token && sl.source === 'auto').map(sl => String(sl.token)),
    ...(_bs?.watchSource === 'auto' && _bs?.currentToken ? [String(_bs.currentToken)] : []),
  ]
  const _autoKey = [...new Set(_autoTokens)].sort().join(',')
  useEffect(() => {
    const now = _autoKey ? _autoKey.split(',') : []
    if (!seenRef.current) { seenRef.current = new Set(now); return }   // seed, no alert
    const fresh = now.filter(t => !seenRef.current.has(t))
    now.forEach(t => seenRef.current.add(t))
    if (fresh.length) setFound(fresh[fresh.length - 1])
  }, [_autoKey])
  useEffect(() => {
    if (!found) return
    const id = setTimeout(() => setFound(null), 20000)
    return () => clearTimeout(id)
  }, [found])

  // Tick to keep badge live (status object reads time-based fields)
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!bot?.enabled) return
    const id = setInterval(() => setTick(t => t + 1), 500)
    return () => clearInterval(id)
  }, [bot?.enabled])

  // ─── Broadcast microstructure metrics for WalletHoldings gauge ───
  // Read individual values, not the full status object, so the effect
  // depends on the value primitives (which only change when they really
  // do), preventing a re-render storm.
  const metricsStatus = bot?.getStatus?.()
  const _vacuumScore       = metricsStatus?.vacuumScore
  const _densityRatio      = metricsStatus?.densityRatio
  const _approachVelocity  = metricsStatus?.approachVelocity
  const _tapeHeat          = metricsStatus?.tapeHeat
  const _churnScore        = metricsStatus?.churnScore
  useEffect(() => {
    if (!metricsStatus) return
    window.dispatchEvent(new CustomEvent('solcheddar-bot-metrics', {
      detail: {
        vacuumScore:      _vacuumScore,
        densityRatio:     _densityRatio,
        approachVelocity: _approachVelocity,
        tapeHeat:         _tapeHeat,
        churnScore:       _churnScore,
      }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_vacuumScore, _densityRatio, _approachVelocity, _tapeHeat, _churnScore])

  if (!bot) return null
  const status = bot.getStatus()

  // Disabled bot: SAME badge, same layout — only the state string reads OFF
  // and the mark goes gray/static. Nulling here locked the user out: the
  // badge is the menu's only entry point, so a disabled bot could never be
  // re-enabled from the chart.
  const botOff = !bot.enabled
  const walletLocked = !walletConnected
  const baseQualified = status.activeBase?.qualified
  const dotColor = botOff ? '#666' : waiting ? '#ffb300' : walletLocked ? '#ff5252'
    : baseQualified ? '#448aff' : (STATE_COLORS[status.state] || '#888')
  const dotPulsing = !botOff && (waiting || walletLocked || baseQualified)

  // In a position → live unrealized P/L; flat → realized daily. The old
  // always-daily read meant LOADED sat at +0.0% until the exit booked.
  const _open = openPnl(status)
  const _inPos = status.state === STATES.PILOT || status.state === STATES.LOADED
  const _shown = (_inPos && _open !== null) ? _open : status.dailyPnL
  const pnlStr = (_shown >= 0 ? '+' : '') + nf(_shown * 100, 1) + '%'
  const pnlColor = _shown >= 0 ? '#66bb6a' : '#ef5350'

  return (
    <div
      className={`bot-badge${compact ? ' compact' : ''}`}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e) }}
    >
      <style>{`
        .bot-badge {
          position: absolute;
          top: 5px;
          left: 8px;
          direction: ltr; /* botbadge-ltr-20261023: chart wrapper is rtl; read left to right, icon first */
          display: flex;
          flex-direction: column;
          gap: 2px;
          background: rgba(54, 54, 54, 0.38);
          padding: 4px 10px;
          border-radius: 3px;
          z-index: 6;
          pointer-events: auto;
          cursor: context-menu;
          font-family: 'JetBrains Mono', monospace;
          min-width: 200px;
        }
        .bot-badge-line {
          display: flex;
          align-items: center;
          gap: 16px; /* botbadge-gap-20261023: +10px icon-text gap */
          font-size: 11px;
          color: #ccc;
        }
        .bot-badge-dot {
          width: 8px; height: 8px; border-radius: 50%;
          flex-shrink: 0;
        }
        .bot-badge-icon {
          flex-shrink: 0;
          display: block;
          cursor: pointer;
        }
        .bot-badge.compact { min-width: 0; } /* same padding open/closed so the icon never moves */
        /* badge-stream-20261023: icon pinned at the corner; messages stream
           out to the right in an overlay, so the badge never resizes. */
        .bot-badge-stream { position: absolute; left: 100%; top: 0;
          display: flex; flex-direction: column; align-items: flex-start; gap: 1px; padding-left: 16px; white-space: nowrap; pointer-events: none; }
        .bot-badge-flash { font-size: 10px; color: #ccc; white-space: nowrap; text-shadow: 0 0 3px #000;
          animation: bot-type 0.6s steps(24, end) forwards, bot-flash 5s ease-out forwards; }
        @keyframes bot-type { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
        @keyframes bot-flash { 0%, 85% { opacity: 1; } 100% { opacity: 0.2; } }
        .bot-badge-found { font-size: 10px; color: #448aff; cursor: pointer; text-decoration: underline; white-space: nowrap;
          pointer-events: auto; text-shadow: 0 0 3px #000; animation: bot-type 0.6s steps(24, end) forwards; }
        .bot-badge-icon.pulsing { animation: bot-pulse 1.2s ease-in-out infinite; }
        .bot-badge-dot.pulsing { animation: bot-pulse 1.2s ease-in-out infinite; }
        @keyframes bot-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        .bot-badge-state {
          font-weight: 600;
          letter-spacing: 0.04em;
        }
        .bot-badge-right {
          margin-left: auto;
          display: flex;
          gap: 8px;
          font-size: 10px;
        }
        .bot-badge-pill {
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 1px 6px;
          border-radius: 3px;
          line-height: 1.4;
        }
        .bot-badge-waiting { color: #ffb300; font-weight: 600; animation: bot-waiting-pulse 1s ease-in-out infinite; }
        .bot-badge-locked  { color: #ff5252; font-weight: 600; animation: bot-waiting-pulse 1.5s ease-in-out infinite; }
        @keyframes bot-waiting-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Line 1: state + daily */}
      <div className="bot-badge-line" style={compact ? { position: 'relative' } : undefined}>
        {/* Robot-head mark IS the status indicator: tinted to the live
            status color and pulsing when active (replaces the old dot). */}
        <svg className={`bot-badge-icon ${dotPulsing ? 'pulsing' : ''}`}
          onClick={toggleCompact}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke={dotColor} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Cheddar Bot">
          <title>Cheddar Bot</title>
          <path d="M12 2v3" />
          <circle cx="12" cy="2" r="1" fill={dotColor} stroke="none" />
          <rect x="4" y="7" width="16" height="12" rx="3" />
          <circle cx="9" cy="13" r="1.4" fill={dotColor} stroke="none" />
          <circle cx="15" cy="13" r="1.4" fill={dotColor} stroke="none" />
        </svg>
        {compact ? (
          <span className="bot-badge-stream">
            {flashes.map(f => <span key={f.key} className="bot-badge-flash">{f.text}</span>)}
            {found && (
              <span className="bot-badge-found" title={found}
                onClick={(e) => { e.stopPropagation(); chartToken(found); setFound(null) }}>
                new: {found.slice(0, 4)}…{found.slice(-4)}
              </span>
            )}
          </span>
        ) : (<>
        <span className="bot-badge-state" style={botOff ? { color: '#777' } : undefined}>{botOff ? 'OFF' : status.state}</span>
        <div className="bot-badge-right">
          <span style={{ color: pnlColor }}>{pnlStr}</span>
          <span style={{ color: '#888' }}>T:{status.dailyTradeCount}</span>
        </div>
        </>)}
      </div>

      {!compact && (<>

      {/* Line 2: state-specific detail */}
      <div className="bot-badge-line">
        {waiting && !botOff ? (
          <span className="bot-badge-waiting">Waiting for wallet...</span>
        ) : walletLocked && !botOff ? (
          <span className="bot-badge-locked">Wallet locked</span>
        ) : (
          <>
            {status.state === STATES.SCANNING && (
              <>
                {status.activeBase ? (
                  <span style={{ color: baseQualified ? '#448aff' : '#888' }}>
                    Base {Math.floor(status.activeBase.durationSec / 60)}m
                    {' '}· abs {nf(status.activeBase.absorption, 1)}×
                    {' '}· peak {nf(status.activeBase.peakChurn, 1)}
                    {baseQualified && ' · READY'}
                  </span>
                ) : (
                  <span style={{ color: '#666' }}>Scanning · no base</span>
                )}
              </>
            )}

            {status.state === STATES.PILOT && status.pilot && (
              <>
                <span
                  className="bot-badge-pill"
                  style={{ color: '#ffb300', background: '#ffb30033', border: '1px solid #ffb30044' }}
                >
                  PILOT
                </span>
                <span style={{ color: '#888' }}>
                  @ {fmtVal(status.pilot.mcap)} · {Math.ceil(status.pilot.timeRemainingMs / 60000)}m left
                </span>
              </>
            )}

            {status.state === STATES.LOADED && status.load && (
              <>
                {status.load.adopted ? (
                  <span
                    className="bot-badge-pill"
                    style={{ color: '#8eb6ff', background: '#448aff33', border: '1px solid #448aff66' }}
                    title="Auto-pilot: managing a position you entered manually"
                  >
                    AUTO
                  </span>
                ) : (
                  <span
                    className="bot-badge-pill"
                    style={{ color: '#00c853', background: '#00c85333', border: '1px solid #00c85344' }}
                  >
                    LOADED
                  </span>
                )}
                <span style={{ color: '#888' }}>
                  basis {fmtVal(status.load.blendedBasis)} · {nf(status.load.peakR, 1)}R
                </span>
              </>
            )}

            {status.state === STATES.COOLDOWN && (
              <span style={{ color: '#777' }}>
                Cooldown {nf(status.cooldownRemaining / 1000, 0)}s
              </span>
            )}
          </>
        )}
      </div>

      {/* Line 3: liquidity reaction speed (display only). Sweep of the prior
          10×1m high/low → seconds until price is back inside by 0.5 ATR;
          'hold' = no reclaim in 120s. md = median of the last 8 reclaims. */}
      {status.liqReaction && (
        <div
          className="bot-badge-line"
          title={'Liquidity reaction: when price sweeps the prior 10-bar (1m) high/low, seconds until it is back inside by 0.5×ATR. hold = no reclaim within 120s. md = median of the last 8 reclaims; h = holds of the last 8 sweeps. Display only.'}
        >
          {(() => {
            const lr = status.liqReaction
            const arrow = (side) => side === 'low' ? '↓' : '↑'
            const last = lr.pending
              ? <span style={{ color: '#ffb300' }}>{arrow(lr.pending.side)}swept</span>
              : lr.last
                ? <span style={{ color: lr.last.sec == null ? '#ef5350' : lr.last.sec <= 15 ? '#66bb6a' : '#ccc' }}>
                    {arrow(lr.last.side)}{lr.last.sec == null ? 'hold' : `${nf(lr.last.sec, 0)}s`}
                  </span>
                : null
            return (
              <>
                <span style={{ color: '#888' }}>liq</span>
                {last}
                {lr.medianSec != null && <span style={{ color: '#888' }}>· md {nf(lr.medianSec, 0)}s</span>}
                {lr.n > 0 && <span style={{ color: '#666' }}>· h {lr.holds}/{lr.n}</span>}
              </>
            )
          })()}
        </div>
      )}
      </>)}
    </div>
  )
}

// (scrollperf) FPCC re-renders on every Y-wheel glide frame (yZoomFactor is its
// state) and every X-zoom candle step, and this menu is always mounted. Each of
// those renders ran the pre-return localStorage position scan (otherPositions /
// readSolPosition) even while HIDDEN, where the output is null. Hidden -> hidden
// re-renders are skipped; any render where it is (or becomes) shown compares
// nothing and always re-renders, so the open menu behaves exactly as before.
// Props that hidden-time effects/listeners key on (bot, dataSource, hlPair, mode)
// still re-render it while hidden. The inline callbacks and x/y are only read
// while shown.
function botMenuPropsEqual(prev, next) {
  if (prev.show || next.show) return false
  return prev.bot === next.bot
    && prev.dataSource === next.dataSource
    && prev.hlPair === next.hlPair
    && prev.mode === next.mode
}
export default memo(BotSettingsMenu, botMenuPropsEqual)
